import { useEffect, useRef, useState } from "react";
import { CarLogo } from "../components/common/CarLogo.js";
import { LanguageSwitcher } from "../components/common/LanguageSwitcher.js";
import { formatFrenchPlate } from "../lib/plate-format.js";
import { CameraScanner, cameraAoVivoDisponivel } from "../components/portal/CameraScanner.js";
import { useAuth } from "../hooks/useAuth.js";
import { useI18n } from "../i18n/I18nProvider.js";
import { useT, type MessageKey } from "../i18n/index.js";
import type { Locale } from "../i18n/messages.js";
import { trDepose, trMethod, trNote, trPanel } from "../lib/devis-i18n.js";
import {
  ChecklistExtras,
  checklistComplete,
  checklistMissing,
  photoSlotLabelKey,
  EMPTY_CHECKLIST_DRAFT,
  type ChecklistDraft,
  type PhotoSlotKind,
} from "../components/portal/ChecklistExtras.js";
import { compressImageToBase64 } from "../lib/image-compress.js";
import { TechnicianDocsBanner } from "../components/portal/TechnicianDocsBanner.js";
import { clearImpersonationToken } from "../lib/impersonation.js";
import { formatMoney } from "../lib/format-fr.js";
import { trpc } from "../lib/trpc.js";
import { useFmtPrazo } from "../components/domain/MyTasksList.js";
import { useAbaPendente } from "../lib/open-file.js";
import { useQueryClient } from "@tanstack/react-query";

/** CEO 2026-10-11: início do Gestion — janela padrão das listas de carros. */
const GESTION_START = "2026-05-01";

/** Nº da semana ISO (o técnico fala "semaine 43"). */
function semanaIso(d: Date): number {
  const x = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dia = x.getUTCDay() || 7;
  x.setUTCDate(x.getUTCDate() + 4 - dia);
  const ano1 = new Date(Date.UTC(x.getUTCFullYear(), 0, 1));
  return Math.ceil(((x.getTime() - ano1.getTime()) / 86400000 + 1) / 7);
}

/**
 * F-tech-portal (F2 do handoff eDSP→Gestion) — portal do TÉCNICO.
 *
 * Tela única, mobile-first, fora do ErpShell: o técnico (papel `technician`,
 * sem nenhum acesso ao ERP) digita a placa, encontra o carro importado do
 * devis (F1) e ASSUME o serviço — PDR / PDR+Montagem (técnico PDR) ou
 * Montagem (montador). A escolha cria as linhas de serviço no dossier.
 */
export default function TechnicianPortal() {
  const t = useT();
  const { locale } = useI18n();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const meQuery = trpc.technicianPortal.me.useQuery(undefined, { enabled: !!user });
  const companies = meQuery.data?.companies ?? [];
  const [companyId, setCompanyId] = useState<number | null>(null);
  const effectiveCompanyId = companyId ?? companies[0]?.companyId ?? null;
  const me = companies.find((c) => c.companyId === effectiveCompanyId) ?? null;
  // F-portal-consulta (CEO 2026-10-05) — técnico que saiu: só consulta os
  // carros (pagamentos/recebimentos), sem busca, importação nem ações.
  const somenteConsulta = me?.readOnly === true;

  // F-tarefas-popup (CEO 2026-10-06) — as tarefas pendentes abrem em POP-UP
  // ao entrar no portal; o técnico pode fechar e seguir consultando, mas ao
  // tentar assumir/lançar um carro novo com PDR pendente o pop-up volta.
  const docsPendQ = trpc.technicianPortal.myDocs.useQuery(
    { companyId: effectiveCompanyId ?? 0 },
    { enabled: effectiveCompanyId != null && !somenteConsulta },
  );
  const pdrPendentes = somenteConsulta ? [] : (meQuery.data?.pdrPendente ?? []);
  // F-carros-abertos (CEO 2026-10-10) — PDR feito, carro não finalizado: avisa
  // a cada entrada, sem "Ciente".
  const carrosAbertos = somenteConsulta ? [] : (meQuery.data?.carrosAbertos ?? []);
  const docsPend =
    docsPendQ.data != null && docsPendQ.data.dueAt != null && docsPendQ.data.missing.length > 0;
  // F-recado-portal — recados do escritório ficam no pop-up até "Ciente".
  const recados = meQuery.data?.notices ?? [];
  // F-recado-trava (CEO 2026-10-28) — recado que trava: sem carro novo,
  // importação nem scanner até o escritório liberar.
  const recadosTravando = somenteConsulta ? [] : recados.filter((r) => r.blocking);
  const nTarefas = recados.length + pdrPendentes.length + carrosAbertos.length + (docsPend ? 1 : 0);
  // F-tarefas (CEO 2026-10-29) — tarefas do escritório (atrasadas + de hoje)
  // entram no mesmo pop-up e no botão; sozinhas NÃO abrem o pop-up (aviso
  // discreto, como o sino no Gestion).
  const tarefasEscQ = trpc.tasks.mine.useQuery(undefined, {
    enabled: !somenteConsulta,
    refetchInterval: 5 * 60_000,
  });
  const tarefasEsc = (tarefasEscQ.data?.rows ?? []).filter(
    (r) => r.bucket !== "upcoming" && r.occurrenceId != null,
  );
  const nPendencias = nTarefas + tarefasEsc.length;
  // Abre sozinho só com as que ainda não receberam "Ver depois" desde o login.
  const nTarefasPopup = tarefasEsc.filter((r) => r.popup).length;
  const [tarefasAbertas, setTarefasAbertas] = useState(false);
  const [tarefasAutoAbertas, setTarefasAutoAbertas] = useState(false);
  useEffect(() => {
    if (
      !tarefasAutoAbertas &&
      (nTarefas > 0 || nTarefasPopup > 0) &&
      (!somenteConsulta || recados.length > 0)
    ) {
      setTarefasAbertas(true);
      setTarefasAutoAbertas(true);
    }
  }, [nTarefas, nTarefasPopup, tarefasAutoAbertas, somenteConsulta, recados.length]);
  /** Ação que coloca um carro NOVO na mão do técnico: com PDR pendente, reabre o pop-up. */
  function liberadoParaNovoCarro(): boolean {
    if (recadosTravando.length > 0 || pdrPendentes.length > 0) {
      setTarefasAbertas(true);
      return false;
    }
    return true;
  }

  // F-portal-edsp-search — busca no formato do eDSP: placa (estilo francês)
  // OU VIN, com botão ⇄ para alternar o campo ativo; menu lateral (☰).
  const [mode, setMode] = useState<"plate" | "vin">("plate");
  const [menuOpen, setMenuOpen] = useState(false);
  const [plate, setPlate] = useState("");
  const [vin, setVin] = useState("");
  const [searched, setSearched] = useState<{ value: string; mode: "plate" | "vin" } | null>(null);
  /**
   * F-etapa-fechada (CEO 2026-09-23) — ao concluir uma etapa (ou iniciar o
   * PDR), o card detalhado FECHA, o carro volta como card resumido em "Meus
   * carros" e um pequeno aviso diz o que aconteceu: "Desmontagem finalizada",
   * "Montagem finalizada", "Serviço PDR iniciado/finalizado". Antes o card
   * ficava aberto e o técnico não sabia se tinha terminado.
   */
  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => setToast(null), 6000);
    return () => window.clearTimeout(id);
  }, [toast]);
  const [feedback, setFeedback] = useState<{ tone: "ok" | "warn" | "error"; text: string } | null>(
    null,
  );

  // F-plate-camera — foto da placa/VIN pela câmera → OCR no aparelho →
  // preenche o campo e já busca. Foto ilegível → aviso e digitação manual.
  const [scanning, setScanning] = useState<"plate" | "vin" | null>(null);
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  const photoKindRef = useRef<"plate" | "vin">("plate");
  // F-scanner (brief CEO 2026-10-04) — câmera AO VIVO quando o navegador
  // permite (HTTPS + getUserMedia); senão, a foto de antes.
  const [liveScan, setLiveScan] = useState<"plaque" | "vin" | null>(null);
  function openCamera(kind: "plate" | "vin") {
    // F-recado-trava — o scanner também fica fechado enquanto o portal está travado.
    if (recadosTravando.length > 0 && !liberadoParaNovoCarro()) return;
    if (cameraAoVivoDisponivel()) {
      setLiveScan(kind === "plate" ? "plaque" : "vin");
      return;
    }
    photoKindRef.current = kind;
    photoInputRef.current?.click();
  }
  async function handlePhoto(file: File) {
    const kind = photoKindRef.current;
    setScanning(kind);
    setFeedback(null);
    try {
      const { recognizeFromPhoto } = await import("../lib/plate-ocr.js");
      const result = await recognizeFromPhoto(file, kind);
      if (result) {
        setMode(kind);
        if (kind === "plate") setPlate(result);
        else setVin(result);
        setSearched({ value: result, mode: kind });
      } else {
        setFeedback({ tone: "warn", text: t("techPortal.scanFail") });
      }
    } catch {
      setFeedback({ tone: "error", text: t("techPortal.scanFail") });
    } finally {
      setScanning(null);
    }
  }

  const searchQuery = trpc.technicianPortal.findByPlate.useQuery(
    {
      companyId: effectiveCompanyId ?? 0,
      plate: searched?.value ?? "",
      mode: searched?.mode ?? "plate",
    },
    { enabled: effectiveCompanyId != null && searched != null && searched.value.length >= 4 },
  );
  // Filtros de "Meus carros": CEO 2026-10-11 — padrão = desde 01/05/2026
  // (início do Gestion); semana, mês, tudo ou intervalo de datas à escolha.
  const [claimsPeriod, setClaimsPeriod] = useState<
    "week" | "thisWeek" | "month" | "since" | "all" | "range"
  >("since");
  const [claimsFrom, setClaimsFrom] = useState(GESTION_START);
  const [claimsTo, setClaimsTo] = useState(new Date().toISOString().slice(0, 10));
  const [claimsStatus, setClaimsStatus] = useState<"all" | "pending" | "paid">("all");
  // F-portal-history-all — "Meus carros" agrega TODAS as empresas onde o
  // usuário é técnico (inclui histórico de empresa sem acesso ativo, ex.:
  // comissões pendentes da ES após migrar para a BS).
  const myClaimsQuery = trpc.technicianPortal.myCarsAll.useQuery(
    {
      period: claimsPeriod,
      status: claimsStatus,
      ...(claimsPeriod === "range" ? { from: claimsFrom, to: claimsTo } : {}),
    },
    { enabled: !!user },
  );
  // F-portal-reception-gate — rascunho do check-list de recepção (franquia
  // sim/não + valor TTC + danos), um por carro exibido.
  // F-franchise-late — dossier com o editor tardio de franquia aberto.
  const [lateFranchiseId, setLateFranchiseId] = useState<number | null>(null);
  const [recDraft, setRecDraft] = useState<{
    dossierId: number;
    franchise: "yes" | "no" | "";
    amount: string;
    damages: string;
    /** F2 checklist — km, pára-brisa/ciel, assinatura (fotos sobem na hora). */
    chk: ChecklistDraft;
  } | null>(null);
  // F-atelier-late — dossier antigo ganha o ateliê depois, direto no card.
  const setAtelierMutation = trpc.technicianPortal.setDossierAtelier.useMutation({
    onSuccess: () => utils.technicianPortal.invalidate(),
    onError: (e) => setFeedback({ tone: "error", text: e.message }),
  });
  const saveReceptionMutation = trpc.technicianPortal.saveReceptionCheck.useMutation({
    onSuccess: () => {
      setFeedback({ tone: "ok", text: t("techPortal.recSavedMsg") });
      setRecDraft(null);
      utils.technicianPortal.invalidate();
    },
    onError: (e) => setFeedback({ tone: "error", text: e.message }),
  });
  // F-portal-devis-import — devis de sistema terceiro subido pelo técnico.
  const [importOpen, setImportOpen] = useState(false);
  const [importCustomerId, setImportCustomerId] = useState<number | null>(null);
  /**
   * F-devis-chantier (CEO 2026-09-21) — no chantier o carro do particular é um
   * cliente novo a cada devis; o técnico escolhe o CHANTIER e o cliente sai do
   * nome impresso no PDF. Cliente PRO continua escolhido na lista, com o
   * ateliê (localização) dele.
   */
  const [importChantierId, setImportChantierId] = useState<number | null>(null);
  // F-atelier — ateliê do cliente onde o carro está (obrigatório se houver).
  const [importAtelierId, setImportAtelierId] = useState<number | null>(null);
  const importFileRef = useRef<HTMLInputElement | null>(null);
  const importCustomersQuery = trpc.technicianPortal.customersForImport.useQuery(
    { companyId: effectiveCompanyId ?? 0 },
    { enabled: effectiveCompanyId != null && importOpen },
  );
  // F-devis-scan — PDF digitalizado: o servidor devolve a imagem da página,
  // o OCR roda AQUI (como na foto) e o técnico confirma a placa antes de
  // importar (o OCR troca letras: Hg772jq → "He772jg").
  const [scanOcr, setScanOcr] = useState<
    | null
    | { stage: "reading" }
    | { stage: "confirm"; text: string; plate: string; fileName: string }
  >(null);
  const scanFileNameRef = useRef<string>("");
  async function handleScanOcr(pagesPng: string[]) {
    setScanOcr({ stage: "reading" });
    setFeedback({ tone: "ok", text: t("techPortal.importScanReading") });
    try {
      const { recognizeDevisImage } = await import("../lib/plate-ocr.js");
      let text = "";
      for (const b64 of pagesPng) {
        const bin = atob(b64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        const file = new File([bytes], "page.png", { type: "image/png" });
        text += (await recognizeDevisImage(file)) + "\n";
      }
      if (text.replace(/\s+/g, "").length < 50) {
        setScanOcr(null);
        setFeedback({ tone: "error", text: t("techPortal.importScanFail") });
        return;
      }
      const guess = text.match(/\b([A-Za-z]{2})[\s-]?(\d{3})[\s-]?([A-Za-z]{2})\b/);
      const plate = guess ? `${guess[1]}-${guess[2]}-${guess[3]}`.toUpperCase() : "";
      setFeedback(null);
      setScanOcr({ stage: "confirm", text, plate, fileName: scanFileNameRef.current });
    } catch {
      setScanOcr(null);
      setFeedback({ tone: "error", text: t("techPortal.importScanFail") });
    }
  }
  const importDevisMutation = trpc.technicianPortal.importDevisPdf.useMutation({
    onSuccess: (data) => {
      if ("needsOcr" in data && data.needsOcr) {
        void handleScanOcr(data.pagesPng);
        return;
      }
      const label = [data.numDevis, data.plate, data.vehicleMakeModel, data.endCustomerName]
        .filter(Boolean)
        .join(" · ");
      // F-devis-dedupe-aviso — devis já existente vira AVISO destacado (âmbar),
      // não "sucesso": o técnico entende que nada foi duplicado.
      // F-scan-warn-i18n — aviso com código vira texto no idioma do portal;
      // sem código, fica como veio (PT do escritório).
      const codes = ("warningCodes" in data ? data.warningCodes : []) as string[];
      const avisos = data.warnings.map((w, i) => {
        const code = codes[i];
        return code ? t(`techPortal.scanWarn_${code}` as MessageKey) : w;
      });
      setFeedback({
        tone: data.created && data.warnings.length === 0 ? "ok" : "warn",
        text: `${data.created ? t("techPortal.importOkPre") : `⚠ ${t("techPortal.importExistsPre")}`} ${label} (${data.panelsCount} ${t("techPortal.importPanelsWord")}). ${avisos.join(" ")}`,
      });
      utils.technicianPortal.invalidate();
      // Já busca a placa: os botões de assumir aparecem na sequência.
      if (data.plate) {
        setMode("plate");
        setPlate(formatFrenchPlate(data.plate));
        setSearched({ value: data.plate, mode: "plate" });
      }
    },
    onError: (e) => setFeedback({ tone: "error", text: e.message }),
  });
  // F-portal-devis-photo — OCR da foto no aparelho → texto → mesmo import.
  const importPhotoRef = useRef<HTMLInputElement | null>(null);
  const [photoImporting, setPhotoImporting] = useState(false);
  const importDevisTextMutation = trpc.technicianPortal.importDevisText.useMutation({
    onSuccess: (data) => {
      setScanOcr(null);
      const label = [data.numDevis, data.plate, data.vehicleMakeModel, data.endCustomerName]
        .filter(Boolean)
        .join(" · ");
      // F-scan-warn-i18n — aviso com código vira texto no idioma do portal;
      // sem código, fica como veio (PT do escritório).
      const codes = ("warningCodes" in data ? data.warningCodes : []) as string[];
      const avisos = data.warnings.map((w, i) => {
        const code = codes[i];
        return code ? t(`techPortal.scanWarn_${code}` as MessageKey) : w;
      });
      setFeedback({
        tone: data.created && data.warnings.length === 0 ? "ok" : "warn",
        text: `${data.created ? t("techPortal.importOkPre") : `⚠ ${t("techPortal.importExistsPre")}`} ${label} (${data.panelsCount} ${t("techPortal.importPanelsWord")}). ${avisos.join(" ")}`,
      });
      utils.technicianPortal.invalidate();
      if (data.plate) {
        setMode("plate");
        setPlate(formatFrenchPlate(data.plate));
        setSearched({ value: data.plate, mode: "plate" });
      }
    },
    onError: (e) => setFeedback({ tone: "error", text: e.message }),
  });
  async function handleImportPhoto(file: File) {
    if (effectiveCompanyId == null || (importCustomerId == null && importChantierId == null))
      return;
    setFeedback(null);
    setPhotoImporting(true);
    try {
      const { recognizeDevisImage } = await import("../lib/plate-ocr.js");
      const text = await recognizeDevisImage(file);
      if (!text || text.trim().length < 50) {
        setFeedback({ tone: "error", text: t("techPortal.importPhotoFail") });
        return;
      }
      importDevisTextMutation.mutate({
        companyId: effectiveCompanyId,
        customerId: importCustomerId!,
        atelierId: importAtelierId ?? undefined,
        text,
      });
    } catch {
      setFeedback({ tone: "error", text: t("techPortal.importPhotoFail") });
    } finally {
      setPhotoImporting(false);
    }
  }
  async function handleImportPdf(file: File) {
    if (effectiveCompanyId == null || (importCustomerId == null && importChantierId == null))
      return;
    setFeedback(null);
    try {
      const buf = new Uint8Array(await file.arrayBuffer());
      let bin = "";
      const CHUNK = 0x8000;
      for (let i = 0; i < buf.length; i += CHUNK) {
        bin += String.fromCharCode(...buf.subarray(i, i + CHUNK));
      }
      scanFileNameRef.current = file.name;
      importDevisMutation.mutate({
        companyId: effectiveCompanyId,
        ...(importChantierId != null
          ? { chantierId: importChantierId }
          : { customerId: importCustomerId!, atelierId: importAtelierId ?? undefined }),
        fileName: file.name,
        contentBase64: btoa(bin),
      });
    } catch {
      setFeedback({ tone: "error", text: t("techPortal.importReadFail") });
    }
  }
  // F-portal-claim-devis — tocar num carro abre o devis dele (peças/método,
  // sem valores do cliente); toque de novo fecha.
  // A lista é MULTIEMPRESA: o devis tem de ser pedido à empresa DO CARRO, não
  // à selecionada no menu — senão "Dossier não encontrado" no carro da outra.
  const [openClaimId, setOpenClaimId] = useState<{ dossierId: number; companyId: number } | null>(
    null,
  );
  // F-mycars-fotos — qual carro está com as fotos abertas DENTRO do card.
  const [photosCarId, setPhotosCarId] = useState<number | null>(null);
  const claimDevisQuery = trpc.technicianPortal.myClaimDevis.useQuery(
    { companyId: openClaimId?.companyId ?? 0, dossierId: openClaimId?.dossierId ?? 0 },
    { enabled: openClaimId != null },
  );
  // F-chantier-agenda — agenda (somente leitura) do chantier selecionado.
  const [agChantierId, setAgChantierId] = useState<number | null>(null);
  const agChantiersQuery = trpc.chantierAgenda.chantiersForTechnician.useQuery(
    { companyId: effectiveCompanyId ?? 0 },
    { enabled: effectiveCompanyId != null },
  );
  const agChantiers = agChantiersQuery.data?.rows ?? [];
  const effectiveAgChantierId = agChantierId ?? agChantiers[0]?.id ?? null;
  const agFmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const agTo = new Date();
  agTo.setDate(agTo.getDate() + 14);
  const techAgendaQuery = trpc.chantierAgenda.listForTechnician.useQuery(
    {
      companyId: effectiveCompanyId ?? 0,
      chantierId: effectiveAgChantierId ?? 0,
      from: agFmt(new Date()),
      to: agFmt(agTo),
    },
    { enabled: effectiveCompanyId != null && effectiveAgChantierId != null },
  );
  const utils = trpc.useUtils();
  const claimMutation = trpc.technicianPortal.claim.useMutation({
    onSuccess: (data) => {
      const parts = data.createdLines
        .map((l) => (l.serviceType === "dsp" ? "PDR" : t("techPortal.montageShort")))
        .join(" + ");
      // F-i18n-franchise-toast — mensagem da franquia montada no idioma do
      // técnico a partir do dado estruturado do servidor.
      const fa = data.franchiseApplied;
      const faMsg = fa
        ? ` ${t("techPortal.claimFranchisePre")} ${formatMoney(fa.ttc, { decimals: 2 })} TTC (${formatMoney(fa.ht, { decimals: 2 })} HT) ${fa.particular ? t("techPortal.claimFranchiseSufParticular") : t("techPortal.claimFranchiseSuf")}`
        : "";
      setFeedback({
        tone: data.warnings.length > 0 || fa ? "warn" : "ok",
        text: `${t("techPortal.claimOkPre")} ${parts}.${faMsg} ${data.warnings.join(" ")}`,
      });
      utils.technicianPortal.invalidate();
      // F-etapa-fechada — "serviço PDR iniciado" / "desmontagem assumida", e o
      // carro sai do card detalhado para a lista.
      setToast(
        data.createdLines.some((l) => l.serviceType === "dsp")
          ? t("techPortal.toastPdrStarted")
          : data.demontageConcluded
            ? // F-fotos-antes-do-claim — as fotos já tiradas concluíram a 1ª etapa.
              `${t("techPortal.phaseDoneDemontageMsg")}${
                data.demontageAmount != null
                  ? ` · ${formatMoney(data.demontageAmount, { decimals: 2 })}`
                  : ""
              }`
            : t("techPortal.toastDemontageTaken"),
      );
      fecharCarro();
    },
    onError: (e) => {
      if (e.message.includes("Finalize o PDR")) setTarefasAbertas(true);
      setFeedback({ tone: "error", text: e.message });
    },
  });
  // F-pdr-fim — "serviço PDR finalizado".
  const ackNoticeMut = trpc.technicianPortal.ackNotice.useMutation({
    onSuccess: () => utils.technicianPortal.me.invalidate(),
  });
  // F-recados-equipe (CEO 2026-10-28) — "Feito" encerra; recados da equipe
  // (erp_notices) usam as mutations do erpNotices.
  const doneNoticeMut = trpc.technicianPortal.doneNotice.useMutation({
    onSuccess: () => utils.technicianPortal.me.invalidate(),
  });
  const erpAckMut = trpc.erpNotices.ack.useMutation({
    onSuccess: () => utils.technicianPortal.me.invalidate(),
  });
  const erpDoneMut = trpc.erpNotices.done.useMutation({
    onSuccess: () => utils.technicianPortal.me.invalidate(),
  });
  const fmtPrazoTarefa = useFmtPrazo();
  const taskDoneMut = trpc.tasks.complete.useMutation({
    onSuccess: () => utils.tasks.mine.invalidate(),
  });
  const taskSnoozeMut = trpc.tasks.snooze.useMutation({
    onSuccess: () => {
      void utils.tasks.mine.invalidate();
      if (nTarefas === 0) setTarefasAbertas(false);
    },
  });
  const recadoBusy =
    ackNoticeMut.isPending ||
    doneNoticeMut.isPending ||
    erpAckMut.isPending ||
    erpDoneMut.isPending;
  // CEO 2026-10-12 — "dégarnissage feito pelo garage" sem linha ainda.
  const degGarageMut = trpc.technicianPortal.setDegarnissageExternal.useMutation({
    onSuccess: () => {
      utils.technicianPortal.invalidate();
      setToast(t("techPortal.degarnissageGarageDone"));
    },
    onError: (e) => setFeedback({ tone: "error", text: e.message }),
  });
  const dspDoneMut = trpc.technicianPortal.setDspDone.useMutation({
    onSuccess: () => {
      utils.technicianPortal.invalidate();
      setToast(t("techPortal.toastPdrDone"));
      fecharCarro();
    },
    onError: (e) => setFeedback({ tone: "error", text: e.message }),
  });
  function fecharCarro() {
    setSearched(null);
    setPlate("");
    setVin("");
    void utils.technicianPortal.myCarsAll.invalidate();
  }
  function concluiuFase(phase: "demontage" | "montage", amount: number | null) {
    const valor = amount != null ? ` · ${formatMoney(amount, { decimals: 2 })}` : "";
    setToast(
      (phase === "demontage"
        ? t("techPortal.toastDemontageDone")
        : t("techPortal.toastMontageDone")) + valor,
    );
    fecharCarro();
  }
  // F-impersonate-tab — encerrar a visão: audita (logout com Bearer NÃO limpa
  // o cookie do admin), limpa o token DESTA aba e fecha-a (fallback: a aba
  // recarrega como admin, pelo cookie intacto).
  const endImpersonationMut = trpc.auth.logout.useMutation({
    onSettled: () => {
      clearImpersonationToken();
      window.close();
      setTimeout(() => window.location.assign("/dashboard"), 300);
    },
  });
  // F-locale-uk — persiste a preferência de idioma escolhida no portal.
  const updateLocaleMutation = trpc.auth.updateLocale.useMutation();
  function handleLocaleChange(next: Locale) {
    updateLocaleMutation.mutate({ locale: next });
  }

  const logoutMutation = trpc.auth.logout.useMutation({
    onSettled: () => {
      queryClient.clear();
      window.location.assign("/login");
    },
  });

  const isMontador = me?.serviceRole === "montage";
  const dossiers = searchQuery.data?.dossiers ?? [];
  const myClaims = myClaimsQuery.data?.rows ?? [];

  // F-portal-busca-primeiro (auditoria UX 23/10/2026, item 7 — P1/P2/P3; CEO
  // 24/10 "pode seguir"): a busca da placa é a primeira coisa da tela e só
  // ela; "Mes voitures" vira cartões com a SITUAÇÃO do carro e a próxima ação
  // (antes só mostrava o dinheiro); filtros em dois seletores; barra inferior
  // com o scanner no centro; nenhum texto abaixo de 12 px.
  const periodoLabel =
    claimsPeriod === "week"
      ? t("techPortal.filterWeek")
      : claimsPeriod === "thisWeek"
        ? t("techPortal.filterThisWeek")
        : claimsPeriod === "month"
          ? t("techPortal.filterMonth")
          : claimsPeriod === "since"
            ? t("techPortal.filterSince")
            : claimsPeriod === "range"
              ? `${claimsFrom} → ${claimsTo}`
              : t("techPortal.prodAllTime");
  const chantierAtual = agChantiers.find((ch) => ch.id === effectiveAgChantierId)?.name ?? null;
  // Selo da empresa no cartão só quando a lista mistura empresas (histórico
  // ES + BS); e em sigla, para o nome do cliente caber inteiro.
  const multiEmpresa = new Set(myClaims.map((c) => c.companyId)).size > 1;
  const siglaEmpresa = (nome: string) =>
    nome
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0]!.toUpperCase())
      .join("")
      .slice(0, 4);
  type SitPortal = {
    label: string;
    tone: "lime" | "amber" | "sky" | "neutral";
    icone: string | null;
    acao: "pdr" | "phases" | null;
    testid: string;
  };
  const TONE_SIT: Record<SitPortal["tone"], string> = {
    lime: "border-lime/40 bg-lime/15 text-lime",
    amber: "border-amber-500/40 bg-amber-500/15 text-amber-300",
    sky: "border-sky-500/40 bg-sky-500/15 text-sky-300",
    neutral: "border-neutral-600 bg-neutral-800 text-neutral-200",
  };
  /** O que o técnico precisa saber do carro numa olhada, e o que falta fazer. */
  function situacaoPortal(c: (typeof myClaims)[number]): SitPortal {
    const dsp = c.services.find((sv) => sv.serviceType === "dsp" && sv.mine);
    if (dsp && !dsp.dspDone) {
      return {
        label: t("techPortal.sitInRepair"),
        tone: "amber",
        icone: "🔧",
        acao: "pdr",
        testid: "mycars-sit-repair",
      };
    }
    const mont = c.services.find(
      (sv) =>
        sv.serviceType === "montage_demontage" &&
        ((sv.demontageMine && !sv.demontageDone) || (sv.montageMine && !sv.montageDone)),
    );
    if (mont) {
      return {
        label: t("techPortal.sitMontage"),
        tone: "amber",
        icone: "🔧",
        acao: "phases",
        testid: "mycars-sit-montage",
      };
    }
    if (c.received) {
      return {
        label: t("techPortal.rowPaid"),
        tone: "lime",
        icone: "💶",
        acao: null,
        testid: "mycars-paid",
      };
    }
    if ((c.paidAmount ?? 0) > 0) {
      return {
        label: t("techPortal.rowPaidPartial"),
        tone: "sky",
        icone: "💶",
        acao: null,
        testid: "mycars-paid-partial",
      };
    }
    return {
      label: t("techPortal.sitDone"),
      tone: "neutral",
      icone: null,
      acao: null,
      testid: "mycars-sit-done",
    };
  }
  /** Abre o carro na busca para marcar dépose/repose (placa OU VIN; pode ser da outra empresa). */
  function abrirFases(c: { plate: string | null; vin: string | null; companyId: number }) {
    const valor = c.plate ?? c.vin ?? "";
    const modo: "plate" | "vin" = c.plate ? "plate" : "vin";
    setCompanyId(c.companyId);
    setPlate(valor);
    setSearched({ value: valor, mode: modo });
    setMode(modo);
    // depois do re-render (o card da busca aparece no topo)
    setTimeout(() => window.scrollTo({ top: 0 }), 60);
  }
  const irPara = (id: string) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  function abrirImportacao() {
    if (!importOpen && !liberadoParaNovoCarro()) return;
    setImportOpen(true);
    window.setTimeout(
      () =>
        document
          .querySelector("[data-testid=devis-import]")
          ?.scrollIntoView({ behavior: "smooth", block: "start" }),
      60,
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100">
      <header className="sticky top-0 z-10 border-b border-neutral-800 bg-neutral-900/95 px-4 py-2.5 backdrop-blur">
        <div className="mx-auto flex max-w-xl items-center justify-between gap-3">
          <div className="min-w-0">
            <h1
              className="truncate text-base font-semibold tracking-wide"
              data-testid="portal-title"
            >
              <span className="text-lime">{me?.technicianName ?? user.name}</span>
            </h1>
            <p className="truncate text-xs text-neutral-400" data-testid="portal-subtitle">
              {me
                ? isMontador
                  ? t("techPortal.roleMontador")
                  : t("techPortal.rolePdr")
                : t("techPortal.title")}
              {` · ${t("techPortal.weekWord")} ${semanaIso(new Date())}`}
              {chantierAtual && ` · ${chantierAtual}`}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {/* F-locale-uk — técnico escolhe o idioma direto no portal (Roman). */}
            <LanguageSwitcher size="sm" onChange={handleLocaleChange} />
            <button
              onClick={() => setMenuOpen(true)}
              aria-label={t("techPortal.menuBtnAria")}
              data-testid="portal-menu-btn"
              className="rounded-md border border-neutral-700 px-2.5 py-1.5 text-base leading-none hover:border-lime"
            >
              ☰
            </button>
          </div>
        </div>
      </header>

      {/* Menu lateral (estilo eDSP) */}
      {menuOpen && (
        <div className="fixed inset-0 z-20">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMenuOpen(false)} />
          <nav className="absolute left-0 top-0 bottom-0 w-72 max-w-[80vw] bg-neutral-900 border-r border-neutral-800 p-4 flex flex-col gap-1">
            <button
              onClick={() => setMenuOpen(false)}
              aria-label="close"
              className="self-start rounded-md px-2 py-1 text-lg text-neutral-400 hover:text-neutral-100"
            >
              ✕
            </button>
            <p className="px-2 pb-3 text-sm font-semibold">
              <span className="text-lime">STORM</span> · {t("techPortal.title")}
            </p>
            {companies.length > 1 && (
              <select
                value={effectiveCompanyId ?? ""}
                onChange={(e) => setCompanyId(Number(e.target.value))}
                className="mb-2 rounded-md border border-neutral-700 bg-neutral-900 px-2 py-2 text-sm"
              >
                {companies.map((c) => (
                  <option key={c.companyId} value={c.companyId}>
                    {c.companyName}
                  </option>
                ))}
              </select>
            )}
            <button
              onClick={() => {
                setMenuOpen(false);
                setSearched(null);
                setPlate("");
                setVin("");
                setFeedback(null);
                window.scrollTo({ top: 0 });
              }}
              className="rounded-md px-3 py-2.5 text-left text-sm hover:bg-neutral-800"
            >
              🏠 {t("techPortal.menuHome")}
            </button>
            <button
              onClick={() => {
                setMenuOpen(false);
                document.getElementById("mes-carros")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="rounded-md px-3 py-2.5 text-left text-sm hover:bg-neutral-800"
            >
              🚗 {t("techPortal.myCarsTitle")}
            </button>
            {(
              [
                ["since", "all", t("techPortal.filterSince")],
                ["thisWeek", "all", t("techPortal.filterThisWeek")],
                ["week", "all", t("techPortal.filterWeek")],
                ["all", "all", t("techPortal.filterAll")],
                ["all", "paid", t("techPortal.statusPaid")],
                ["all", "pending", t("techPortal.statusPending")],
              ] as const
            ).map(([p, s, label]) => (
              <button
                key={`${p}-${s}`}
                onClick={() => {
                  setClaimsPeriod(p);
                  setClaimsStatus(s);
                  setMenuOpen(false);
                  document.getElementById("mes-carros")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="rounded-md px-3 py-2 pl-9 text-left text-xs text-neutral-400 hover:bg-neutral-800 hover:text-neutral-100"
              >
                {label}
              </button>
            ))}
            <div className="mt-auto border-t border-neutral-800 pt-2">
              <button
                onClick={() => logoutMutation.mutate()}
                className="w-full rounded-md px-3 py-2.5 text-left text-sm text-red-300 hover:bg-neutral-800"
              >
                ⏻ {t("techPortal.logout")}
              </button>
            </div>
          </nav>
        </div>
      )}

      <main className="max-w-xl mx-auto p-4 pb-28 space-y-5">
        {/* F-tarefas-popup — documentos legais, carros a finalizar (sênior) e
            PDR sem "finalizado" ficam no pop-up; aqui só o atalho para reabrir. */}
        {nPendencias > 0 && !somenteConsulta && (
          <button
            type="button"
            onClick={() => setTarefasAbertas(true)}
            className="flex w-full items-center justify-between rounded-lg border border-amber-500/50 bg-amber-500/10 px-4 py-2.5 text-left text-sm font-semibold text-amber-200"
            data-testid="pending-tasks-btn"
          >
            <span>
              ⚠ {t("techPortal.pendingTasksBtn")} ({nPendencias})
            </span>
            <span aria-hidden="true">›</span>
          </button>
        )}
        {tarefasAbertas && effectiveCompanyId != null && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            role="dialog"
            aria-modal="true"
            aria-label={t("techPortal.pendingTasksTitle")}
            data-testid="pending-tasks-modal"
          >
            <div className="max-h-[90vh] w-full max-w-md space-y-3 overflow-y-auto rounded-xl border border-amber-500/50 bg-neutral-950 p-4">
              <div className="flex items-start justify-between gap-2">
                <h2 className="text-base font-bold text-amber-200">
                  ⚠ {t("techPortal.pendingTasksTitle")}
                </h2>
                <button
                  type="button"
                  onClick={() => setTarefasAbertas(false)}
                  className="text-xl leading-none text-neutral-400 hover:text-neutral-200"
                  aria-label={t("techPortal.pendingTasksClose")}
                >
                  ×
                </button>
              </div>
              {recados.map((r) =>
                r.blocking ? (
                  <div
                    key={r.id}
                    className="space-y-2 rounded-md border border-red-500/60 bg-red-500/10 px-3 py-3"
                    data-testid="portal-notice-blocking"
                  >
                    <p className="text-xs font-semibold uppercase tracking-wider text-red-300">
                      🔒 {t("techPortal.noticeBlockedTitle")} ·{" "}
                      {new Date(r.createdAt).toLocaleDateString("fr-FR")}
                    </p>
                    <p className="text-sm font-semibold text-red-100">
                      {t("techPortal.noticeBlockedHint")}
                    </p>
                    <p className="whitespace-pre-line text-sm text-red-50">{r.message}</p>
                    {r.acknowledgedAt ? (
                      <p className="text-xs text-red-200/80">{t("techPortal.noticeBlockedWait")}</p>
                    ) : (
                      <button
                        type="button"
                        disabled={ackNoticeMut.isPending}
                        onClick={() => ackNoticeMut.mutate({ noticeId: r.id })}
                        className="rounded-md bg-red-500 px-3 py-1 text-xs font-semibold text-white disabled:opacity-40"
                      >
                        ✓ {t("techPortal.noticeAckBtn")}
                      </button>
                    )}
                  </div>
                ) : (
                  <div
                    key={r.id}
                    className="space-y-2 rounded-md border border-sky-500/50 bg-sky-500/10 px-3 py-3"
                    data-testid="portal-notice"
                  >
                    <p className="text-xs font-semibold uppercase tracking-wider text-sky-300">
                      📣 {t("techPortal.noticeTitle")} ·{" "}
                      {new Date(r.createdAt).toLocaleDateString("fr-FR")}
                    </p>
                    <p className="whitespace-pre-line text-sm text-sky-50">{r.message}</p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={recadoBusy}
                        onClick={() =>
                          (r.source === "erp"
                            ? r.info
                              ? erpDoneMut
                              : erpAckMut
                            : ackNoticeMut
                          ).mutate({ noticeId: r.id })
                        }
                        className="rounded-md border border-sky-400 px-3 py-1 text-xs font-semibold text-sky-100 disabled:opacity-40"
                        data-testid="portal-notice-ack"
                      >
                        ✓ {t("techPortal.noticeAckBtn")}
                      </button>
                      {!(r.source === "erp" && r.info) && (
                        <button
                          type="button"
                          disabled={recadoBusy}
                          onClick={() =>
                            (r.source === "erp" ? erpDoneMut : doneNoticeMut).mutate({
                              noticeId: r.id,
                            })
                          }
                          className="rounded-md bg-lime px-3 py-1 text-xs font-semibold text-on-lime disabled:opacity-40"
                          data-testid="portal-notice-done"
                        >
                          ✔ {t("techPortal.noticeDoneBtn")}
                        </button>
                      )}
                    </div>
                    {!(r.source === "erp" && r.info) && (
                      <p className="text-[11px] text-sky-200/80">{t("techPortal.noticeAckHint")}</p>
                    )}
                  </div>
                ),
              )}
              {tarefasEsc.length > 0 && (
                <div
                  className="space-y-2 rounded-md border border-violet-500/50 bg-violet-500/10 px-3 py-3"
                  data-testid="portal-tasks"
                >
                  <p className="text-sm font-bold text-violet-200">
                    ✅ {t("techPortal.myTasksTitle")} · {tarefasEsc.length}
                  </p>
                  {tarefasEsc.map((r) => (
                    <div
                      key={r.occurrenceId}
                      className="flex items-start justify-between gap-2 rounded-md border border-violet-500/30 px-2 py-1.5"
                      data-testid="portal-task"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-violet-50">{r.title}</p>
                        <p
                          className={`text-xs ${r.bucket === "overdue" ? "text-red-300" : "text-violet-200/80"}`}
                        >
                          {r.bucket === "overdue"
                            ? `⚠ ${t("techPortal.myTasksOverdue")}`
                            : t("techPortal.myTasksToday")}{" "}
                          · {t("techPortal.myTasksDue")} {fmtPrazoTarefa(r.dueAt)}
                        </p>
                        {r.description && (
                          <p className="whitespace-pre-line text-xs text-neutral-300">
                            {r.description}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        disabled={taskDoneMut.isPending}
                        onClick={() => taskDoneMut.mutate({ occurrenceId: r.occurrenceId! })}
                        className="shrink-0 rounded-md bg-lime px-3 py-1 text-xs font-semibold text-on-lime disabled:opacity-40"
                        data-testid="portal-task-done"
                      >
                        ✔ {t("techPortal.myTasksDoneBtn")}
                      </button>
                    </div>
                  ))}
                  {taskDoneMut.isError && (
                    <p className="rounded-md bg-red-100 px-3 py-2 text-xs font-semibold text-red-700">
                      ✕ {taskDoneMut.error.message}
                    </p>
                  )}
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      disabled={taskSnoozeMut.isPending}
                      onClick={() =>
                        taskSnoozeMut.mutate({
                          occurrenceIds: tarefasEsc.map((r) => r.occurrenceId!),
                        })
                      }
                      className="rounded-md border border-violet-400/60 px-3 py-1 text-xs font-semibold text-violet-100 disabled:opacity-40"
                      data-testid="portal-tasks-later"
                    >
                      ⏰ {t("techPortal.myTasksLaterBtn")}
                    </button>
                  </div>
                  <p className="text-[11px] text-violet-200/80">{t("techPortal.myTasksHint")}</p>
                </div>
              )}
              {pdrPendentes.length > 0 && (
                <div
                  className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-3"
                  data-testid="pdr-pendente"
                >
                  <p className="text-sm font-semibold text-amber-200">
                    {t("techPortal.pdrPendingNote")}
                  </p>
                  {pdrPendentes.map((p) => (
                    <div key={p.dossierId} className="flex items-center justify-between gap-2">
                      <span className="font-mono text-sm text-amber-100">
                        {p.plate ?? `#${p.dossierId}`}
                      </span>
                      <button
                        type="button"
                        disabled={dspDoneMut.isPending}
                        onClick={() =>
                          dspDoneMut.mutate({ companyId: p.companyId, dossierId: p.dossierId })
                        }
                        className="rounded-md bg-lime px-3 py-1 text-xs font-semibold text-on-lime disabled:opacity-40"
                      >
                        ✓ {t("techPortal.pdrDoneBtn")}
                      </button>
                    </div>
                  ))}
                  {/* CEO 2026-10-07: o erro aparecia ATRÁS do pop-up e o botão
                      parecia não fazer nada. */}
                  {dspDoneMut.isError && (
                    <p
                      className="rounded-md bg-red-100 px-3 py-2 text-xs font-semibold text-red-700"
                      data-testid="pdr-done-error"
                    >
                      ✕ {dspDoneMut.error.message}
                    </p>
                  )}
                </div>
              )}
              {carrosAbertos.length > 0 && (
                <div
                  className="space-y-2 rounded-md border border-orange-500/50 bg-orange-500/10 px-3 py-3"
                  data-testid="carros-abertos"
                >
                  <p className="text-sm font-bold text-orange-200">
                    🚗 {t("techPortal.openCarsTitle")} · {carrosAbertos.length}
                  </p>
                  <p className="text-xs text-orange-100/90">{t("techPortal.openCarsNote")}</p>
                  {carrosAbertos.map((c) => (
                    <div
                      key={c.dossierId}
                      className="flex items-center justify-between gap-2 rounded-md border border-orange-500/30 px-2 py-1.5"
                    >
                      <div className="min-w-0">
                        <span className="font-mono text-sm font-semibold text-orange-100">
                          {c.plate ?? c.vin ?? `#${c.dossierId}`}
                        </span>
                        {c.vehicle && (
                          <span className="ml-2 text-xs text-neutral-300">{c.vehicle}</span>
                        )}
                        <p className="text-xs text-orange-300">
                          {c.semMontagem
                            ? t("techPortal.openCarsNoMontage")
                            : `${t("techPortal.openCarsMontage")} ${c.montageTechnician ?? "—"}`}
                        </p>
                      </div>
                      {(c.plate ?? c.vin) && (
                        <button
                          type="button"
                          onClick={() => {
                            setTarefasAbertas(false);
                            // O carro pode estar na OUTRA empresa do técnico;
                            // carro de parc só com VIN busca-se por VIN.
                            const valor = c.plate ?? c.vin ?? "";
                            const modo = c.plate ? "plate" : "vin";
                            setCompanyId(c.companyId);
                            setPlate(valor);
                            setSearched({ value: valor, mode: modo });
                            setMode(modo);
                          }}
                          className="shrink-0 rounded-md border border-orange-400/60 px-3 py-1 text-xs font-semibold text-orange-100 hover:border-orange-300"
                        >
                          {t("techPortal.openCarsOpen")} ›
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {/* F-tech-docs — documentos legais (avisa, não bloqueia). */}
              <TechnicianDocsBanner companyId={effectiveCompanyId} />
              {nPendencias === 0 && (
                <p className="text-sm text-lime">✓ {t("techPortal.pendingTasksNone")}</p>
              )}
              <p className="text-xs text-neutral-400">{t("techPortal.pendingTasksHint")}</p>
              <button
                type="button"
                onClick={() => setTarefasAbertas(false)}
                className="w-full rounded-lg border border-neutral-700 px-4 py-2 text-sm text-neutral-200 hover:border-neutral-500"
              >
                {t("techPortal.pendingTasksClose")}
              </button>
            </div>
          </div>
        )}
        {/* F-impersonate — sessão aberta por um super admin via "Entrar como". */}
        {user?.impersonatedBy && (
          <div
            className="flex items-center justify-between gap-2 rounded-md px-3 py-2 text-xs font-semibold"
            style={{ background: "#D97706", color: "#fff" }}
          >
            <span>
              👁 {t("techPortal.impersonatingPre")} <b>{user.name}</b> (
              {t("techPortal.impersonatingBy")} {user.impersonatedBy.name})
            </span>
            <button
              onClick={() => endImpersonationMut.mutate()}
              disabled={endImpersonationMut.isPending}
              className="shrink-0 rounded border border-white/60 px-2 py-1 font-semibold underline-offset-2 hover:bg-white/10"
            >
              {t("techPortal.impersonatingExit")}
            </button>
          </div>
        )}
        {meQuery.data && !meQuery.data.isTechnician && (
          <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs font-semibold text-amber-200">
            ⚠ {t("techPortal.notTechnicianNote")}
          </p>
        )}
        {me && !me.linked && (
          <p
            className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs font-semibold text-amber-200"
            data-testid="portal-not-linked"
          >
            {t("techPortal.notLinkedNote")}
          </p>
        )}

        {somenteConsulta && (
          <p
            className="rounded-md border border-sky-500/30 bg-sky-500/10 px-4 py-3 text-sm text-sky-200"
            data-testid="portal-read-only"
          >
            🔒 {t("techPortal.readOnlyNote")}
          </p>
        )}
        {!somenteConsulta && (
          <>
            {/* F-portal-busca-primeiro — um campo (Placa | VIN), câmera em
                destaque, um botão. O texto que explicava o ⇄ e o segundo campo
                saíram: no pátio, com luva e sol, cada toque conta. */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setFeedback(null);
                const value =
                  mode === "plate"
                    ? formatFrenchPlate(plate.trim())
                    : vin.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
                if (mode === "plate") setPlate(value);
                else setVin(value);
                setSearched(value.length >= 4 ? { value, mode } : null);
              }}
              className="space-y-3 rounded-2xl border border-neutral-800 bg-neutral-900/80 p-3"
              data-testid="portal-search"
            >
              {/* F-plate-camera — input de câmera compartilhado (placa/VIN). */}
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) void handlePhoto(f);
                }}
              />
              {liveScan && effectiveCompanyId != null && (
                <CameraScanner
                  modo={liveScan}
                  companyId={effectiveCompanyId}
                  onResult={(texto) => {
                    const kind = liveScan === "vin" ? "vin" : "plate";
                    setLiveScan(null);
                    setMode(kind);
                    setFeedback(null);
                    if (kind === "plate") setPlate(texto);
                    else setVin(texto);
                    setSearched({ value: texto, mode: kind });
                  }}
                  onClose={() => setLiveScan(null)}
                />
              )}
              {scanning && (
                <p className="rounded-md border border-lime/30 bg-lime/10 px-3 py-2 text-center text-sm text-lime">
                  📷 {t("techPortal.scanning")}
                </p>
              )}

              {/* Alternância Placa | VIN */}
              <div
                className="grid grid-cols-2 rounded-lg border border-neutral-700 bg-neutral-950 p-0.5 text-sm font-semibold"
                role="tablist"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === "plate"}
                  data-testid="mode-plate"
                  onClick={() => setMode("plate")}
                  className={`rounded-md py-2 ${mode === "plate" ? "bg-lime text-on-lime" : "text-neutral-300"}`}
                >
                  {t("techPortal.modePlate")}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === "vin"}
                  data-testid="mode-vin"
                  onClick={() => setMode("vin")}
                  className={`rounded-md py-2 ${mode === "vin" ? "bg-lime text-on-lime" : "text-neutral-300"}`}
                >
                  VIN
                </button>
              </div>

              <div className="flex items-stretch gap-2">
                {mode === "plate" ? (
                  /* Placa estilo francês */
                  <div className="flex min-w-0 flex-1 items-stretch overflow-hidden rounded-xl border-4 border-blue-600 bg-white">
                    <div className="flex w-8 flex-col items-center justify-center bg-blue-700 text-white">
                      <span className="text-xs leading-none">★</span>
                      <span className="text-lg font-bold leading-tight">F</span>
                    </div>
                    <input
                      value={plate}
                      onChange={(e) => {
                        // F-scanner camada 0 — sem máscara a cada tecla: o "Escanear
                        // Texto" do iOS cola o texto e a máscara o quebrava. Formata
                        // no blur e no envio.
                        const v = e.target.value.toUpperCase();
                        setPlate(v);
                        // F-busca-limpa (CEO 2026-09-21) — apagar a placa tira o carro
                        // da tela.
                        if (v.trim() === "") setSearched(null);
                      }}
                      onBlur={() => setPlate((p) => formatFrenchPlate(p))}
                      placeholder="AA-123-CD"
                      maxLength={20}
                      inputMode="text"
                      autoCapitalize="characters"
                      autoCorrect="off"
                      autoComplete="off"
                      spellCheck={false}
                      autoFocus
                      aria-label={t("techPortal.modePlate")}
                      data-testid="plate-input"
                      className="min-w-0 flex-1 bg-white px-2 py-3 text-center font-mono text-2xl font-semibold tracking-[0.15em] text-neutral-900 placeholder:text-neutral-300 outline-none"
                    />
                    {plate && (
                      <button
                        type="button"
                        onClick={() => {
                          setPlate("");
                          setSearched(null);
                        }}
                        aria-label="clear"
                        className="px-3 text-neutral-400"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ) : (
                  /* VIN */
                  <div className="flex min-w-0 flex-1 items-center gap-2 rounded-xl border-2 border-blue-500/60 bg-neutral-950 px-3">
                    <span className="rounded bg-neutral-800 px-2 py-1 text-xs font-semibold text-neutral-200">
                      VIN
                    </span>
                    <input
                      value={vin}
                      onChange={(e) => {
                        const v = e.target.value.toUpperCase();
                        setVin(v);
                        if (v.trim() === "") setSearched(null);
                      }}
                      onBlur={() => setVin((v) => v.replace(/[^A-Za-z0-9]/g, "").toUpperCase())}
                      inputMode="text"
                      autoCapitalize="characters"
                      autoCorrect="off"
                      autoComplete="off"
                      spellCheck={false}
                      autoFocus
                      placeholder="WWWAUZ123456789"
                      aria-label="VIN"
                      data-testid="vin-input"
                      className="min-w-0 flex-1 bg-transparent py-3 font-mono text-base uppercase tracking-wider outline-none"
                    />
                    {vin && (
                      <button
                        type="button"
                        onClick={() => {
                          setVin("");
                          setSearched(null);
                        }}
                        aria-label="clear"
                        className="px-1 text-neutral-400"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => openCamera(mode)}
                  disabled={scanning != null}
                  aria-label={mode === "plate" ? "scan-plate" : "scan-vin"}
                  title={t("techPortal.scanTitle")}
                  data-testid="scan-camera"
                  className="flex w-16 shrink-0 items-center justify-center rounded-xl bg-lime text-2xl text-on-lime disabled:opacity-50"
                >
                  📷
                </button>
              </div>

              <button
                type="submit"
                disabled={(mode === "plate" ? plate : vin).trim().length < 4}
                data-testid="search-submit"
                className="w-full rounded-xl bg-neutral-100 px-5 py-3 text-base font-semibold text-neutral-900 disabled:opacity-40"
              >
                🔍 {t("techPortal.searchCarBtn")}
              </button>
              {!importOpen && (
                <p className="text-center text-xs text-neutral-400">
                  <button
                    type="button"
                    onClick={abrirImportacao}
                    data-testid="import-link"
                    className="font-semibold text-lime underline underline-offset-2"
                  >
                    {t("techPortal.importLink")}
                  </button>
                </p>
              )}
            </form>

            {toast && (
              <div className="fixed inset-x-0 bottom-24 z-50 flex justify-center px-4">
                <p className="rounded-xl border border-lime/50 bg-neutral-900 px-4 py-3 text-sm font-semibold text-lime shadow-lg">
                  ✓ {toast}
                </p>
              </div>
            )}
            {feedback && (
              <p
                className={`rounded-md px-4 py-3 text-sm border ${
                  feedback.tone === "ok"
                    ? "bg-lime/10 border-lime/30 text-lime"
                    : feedback.tone === "warn"
                      ? "bg-amber-500/10 border-amber-500/30 text-amber-200"
                      : "bg-red-500/10 border-red-500/30 text-red-300"
                }`}
              >
                {feedback.text}
              </p>
            )}

            {searchQuery.isFetching && (
              <p className="text-sm text-neutral-400">{t("techPortal.searching")}</p>
            )}
            {searched != null && !searchQuery.isFetching && dossiers.length === 0 && (
              <div className="rounded-md border border-neutral-800 bg-neutral-900 px-4 py-3 text-sm text-neutral-400">
                {/* F-busca-par (CEO 2026-09-29, GT-961-BG) — o carro EXISTE, só não
                é de cliente onde ele tem par: diz isso primeiro, em destaque,
                no lugar de "nenhum carro" (que o fazia importar de novo). */}
                {searchQuery.data?.semPar ? (
                  <div className="rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-2">
                    <p className="text-sm font-semibold text-amber-200">
                      ⚠ {t("techPortal.noPairTitle")}
                    </p>
                    <p className="mt-0.5 text-xs text-amber-100/90">{t("techPortal.noPairHint")}</p>
                  </div>
                ) : searchQuery.data?.jaFaturado ? (
                  /* CEO 2026-10-26 (GK-294-GV) — o carro existe, mas o serviço
                  dele já foi faturado por outro técnico: é histórico. */
                  <div
                    className="rounded-md border border-sky-500/50 bg-sky-500/10 px-3 py-2"
                    data-testid="busca-ja-faturado"
                  >
                    <p className="text-sm font-semibold text-sky-200">
                      {t("techPortal.alreadyInvoicedTitle")}
                    </p>
                    <p className="mt-0.5 text-xs text-sky-100/90">
                      {t("techPortal.alreadyInvoicedHint")}
                    </p>
                  </div>
                ) : (
                  <p>{t("techPortal.noResults")}</p>
                )}
                {/* F-carro-parc (CEO 2026-09-26) — carro sem devis no Gestion:
                importar o PDF do devis OU lançar como carro de parc (categoria
                pré-cadastrada no cliente + marca/modelo; a placa já veio da busca). */}
                {effectiveCompanyId != null &&
                  // O carro já existe (noutra empresa ou sem devis): nada de criar outro.
                  !searchQuery.data?.outraEmpresa &&
                  !searchQuery.data?.semPar &&
                  (searchQuery.data?.otherCars ?? []).length === 0 && (
                    <ParcCarEntry
                      companyId={effectiveCompanyId}
                      plate={searched.value}
                      mode={searched.mode}
                      liberadoParaNovoCarro={liberadoParaNovoCarro}
                      onOpenImport={() => {
                        if (!liberadoParaNovoCarro()) return;
                        setImportOpen(true);
                        window.setTimeout(
                          () =>
                            document
                              .querySelector("[data-testid=devis-import]")
                              ?.scrollIntoView({ behavior: "smooth", block: "start" }),
                          60,
                        );
                      }}
                      onCreated={(p) => {
                        setFeedback({ tone: "ok", text: `${t("techPortal.parcCreatedPre")} ${p}` });
                        void utils.technicianPortal.invalidate();
                        setSearched({ value: p, mode: "plate" });
                      }}
                    />
                  )}
                {/* F-busca-outra-empresa (CEO 2026-09-21) — quem trabalha nas duas
                empresas procurava com a errada selecionada (o seletor mora no
                menu ☰) e o carro "sumia". Agora a busca diz onde ele está e
                troca de empresa num toque. */}
                {searchQuery.data?.outraEmpresa && (
                  <div className="mt-2 space-y-1.5 border-t border-neutral-800 pt-2">
                    <p className="text-xs text-amber-300">
                      {t("techPortal.foundInOtherCompanyPre")}{" "}
                      <strong>{searchQuery.data.outraEmpresa.companyName}</strong>.
                    </p>
                    <button
                      type="button"
                      onClick={() => setCompanyId(searchQuery.data!.outraEmpresa!.companyId)}
                      className="rounded-md border border-lime px-3 py-1.5 text-xs font-semibold text-lime"
                    >
                      {t("techPortal.switchCompanyBtn")} {searchQuery.data.outraEmpresa.companyName}
                    </button>
                  </div>
                )}
                {/* F-portal-search (CEO 2026-09-20) — a placa EXISTE mas sem devis
                (veio do import de reparações): diz o que é e quem está com o
                carro, em vez de deixar o técnico achando que sumiu. */}
                {(searchQuery.data?.otherCars ?? []).length > 0 && (
                  <div className="mt-2 space-y-1 border-t border-neutral-800 pt-2">
                    <p className="text-xs text-amber-300">{t("techPortal.otherCarsTitle")}</p>
                    {searchQuery.data?.otherCars.map((c) => (
                      <p key={c.dossierCode ?? c.plate} className="text-xs text-neutral-300">
                        <span className="font-mono">{c.plate}</span> · {c.dossierCode ?? "—"} ·{" "}
                        {c.customerName}
                        {c.technicians.length > 0 && (
                          <span className="text-neutral-400">
                            {" "}
                            · {t("techPortal.otherCarsWith")} {c.technicians.join(", ")}
                          </span>
                        )}
                      </p>
                    ))}
                    <p className="text-xs text-neutral-400">{t("techPortal.otherCarsHint")}</p>
                  </div>
                )}
              </div>
            )}

            {/* F-portal-devis-import — devis recebido no LOCAL (sistema do
            cliente, ex.: Repare Grêle) que a Euro Storm não tem: o técnico
            escolhe o cliente e sobe o PDF; o carro entra como "a executar". */}
            <span data-testid="devis-import" />
            {importOpen && (
              <section
                className="rounded-lg border border-neutral-800 bg-neutral-900/60"
                data-testid="devis-import-section"
              >
                <button
                  type="button"
                  onClick={() => setImportOpen(false)}
                  className="flex w-full items-center justify-between px-4 py-3 text-sm"
                >
                  <span>📄 {t("techPortal.importTitle")}</span>
                  <span className="text-neutral-400">▴</span>
                </button>
                {importOpen && (
                  <div className="space-y-3 border-t border-neutral-800 px-4 py-3">
                    <p className="text-xs text-neutral-400">{t("techPortal.importHint")}</p>
                    {importCustomersQuery.data != null &&
                      importCustomersQuery.data.rows.length === 0 && (
                        <p className="rounded bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                          ⚠ {t("techPortal.importNoClients")}
                        </p>
                      )}
                    <select
                      value={
                        importChantierId != null
                          ? `ch:${importChantierId}`
                          : importCustomerId != null
                            ? `c:${importCustomerId}`
                            : ""
                      }
                      onChange={(e) => {
                        const v = e.target.value;
                        setImportAtelierId(null);
                        setImportChantierId(v.startsWith("ch:") ? Number(v.slice(3)) : null);
                        setImportCustomerId(v.startsWith("c:") ? Number(v.slice(2)) : null);
                      }}
                      className="w-full rounded-md border border-neutral-700 bg-neutral-950 px-3 py-2.5 text-sm"
                    >
                      <option value="">{t("techPortal.importClientPlaceholder")}</option>
                      {(importCustomersQuery.data?.chantiers ?? []).map((ch) => (
                        <option key={`ch${ch.id}`} value={`ch:${ch.id}`}>
                          🏗 {ch.name}
                          {ch.city ? ` — ${ch.city}` : ""}
                        </option>
                      ))}
                      {(importCustomersQuery.data?.rows ?? []).map((c) => (
                        <option key={c.id} value={`c:${c.id}`}>
                          {c.displayName}
                        </option>
                      ))}
                    </select>
                    {importChantierId != null && (
                      <p className="rounded bg-neutral-800/60 px-3 py-2 text-xs text-neutral-300">
                        {t("techPortal.importChantierHint")}
                      </p>
                    )}
                    {(() => {
                      const ats = (importCustomersQuery.data?.ateliers ?? []).filter(
                        (a) => a.customerId === importCustomerId,
                      );
                      if (importCustomerId == null || ats.length === 0) return null;
                      return (
                        <select
                          value={importAtelierId ?? ""}
                          onChange={(e) =>
                            setImportAtelierId(e.target.value ? Number(e.target.value) : null)
                          }
                          className="w-full rounded-md border border-neutral-700 bg-neutral-950 px-3 py-2.5 text-sm"
                        >
                          <option value="">{t("techPortal.importAtelierPlaceholder")}</option>
                          {ats.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.name}
                              {a.city ? ` — ${a.city}` : ""}
                            </option>
                          ))}
                        </select>
                      );
                    })()}
                    <input
                      ref={importFileRef}
                      type="file"
                      accept=".pdf,application/pdf"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void handleImportPdf(f);
                        e.target.value = "";
                      }}
                    />
                    <button
                      onClick={() => importFileRef.current?.click()}
                      disabled={
                        (importCustomerId == null && importChantierId == null) ||
                        (importCustomerId != null &&
                          (importCustomersQuery.data?.ateliers ?? []).some(
                            (a) => a.customerId === importCustomerId,
                          ) &&
                          importAtelierId == null) ||
                        importDevisMutation.isPending
                      }
                      className="w-full rounded-lg bg-lime px-5 py-3 text-sm font-semibold text-on-lime disabled:opacity-40"
                    >
                      {importDevisMutation.isPending
                        ? t("techPortal.importing")
                        : `📎 ${t("techPortal.importPickBtn")}`}
                    </button>
                    {/* F-portal-devis-photo — foto/print de tela: OCR no aparelho
                  (tesseract fra), o texto vai para o mesmo parser do PDF. */}
                    <input
                      ref={importPhotoRef}
                      type="file"
                      accept="image/*"
                      data-testid="devis-photo-input"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void handleImportPhoto(f);
                        e.target.value = "";
                      }}
                    />
                    <button
                      onClick={() => importPhotoRef.current?.click()}
                      disabled={
                        importCustomerId == null ||
                        ((importCustomersQuery.data?.ateliers ?? []).some(
                          (a) => a.customerId === importCustomerId,
                        ) &&
                          importAtelierId == null) ||
                        photoImporting
                      }
                      className="w-full rounded-lg border border-lime/50 px-5 py-3 text-sm font-semibold text-lime disabled:opacity-40"
                    >
                      {photoImporting
                        ? t("techPortal.importPhotoReading")
                        : `📷 ${t("techPortal.importPhotoBtn")}`}
                    </button>
                    {scanOcr?.stage === "reading" && (
                      <p className="text-xs text-amber-300" data-testid="scan-reading">
                        ⏳ {t("techPortal.importScanReading")}
                      </p>
                    )}
                    {scanOcr?.stage === "confirm" && (
                      <div
                        className="space-y-2 rounded-lg border border-amber-500/50 bg-amber-500/10 p-3"
                        data-testid="scan-confirm"
                      >
                        <p className="text-xs font-semibold text-amber-200">
                          {t("techPortal.importScanPlatePrompt")}
                        </p>
                        <input
                          value={scanOcr.plate}
                          onChange={(e) =>
                            setScanOcr({ ...scanOcr, plate: e.target.value.toUpperCase() })
                          }
                          placeholder="AA-123-CD"
                          inputMode="text"
                          autoCapitalize="characters"
                          autoCorrect="off"
                          autoComplete="off"
                          spellCheck={false}
                          data-testid="scan-plate"
                          className="w-full rounded-md border border-neutral-700 bg-neutral-900 px-3 py-2 font-mono text-sm uppercase"
                        />
                        <div className="flex gap-2">
                          <button
                            type="button"
                            data-testid="scan-import"
                            disabled={
                              importDevisTextMutation.isPending ||
                              !/^[A-Z]{2}-?\d{3}-?[A-Z]{2}$|^[A-Z0-9-]{4,12}$/.test(
                                scanOcr.plate.trim(),
                              )
                            }
                            onClick={() =>
                              importDevisTextMutation.mutate({
                                companyId: effectiveCompanyId!,
                                ...(importChantierId != null
                                  ? { chantierId: importChantierId }
                                  : {
                                      customerId: importCustomerId!,
                                      atelierId: importAtelierId ?? undefined,
                                    }),
                                text: scanOcr.text,
                                plateOverride: scanOcr.plate.trim(),
                                fileName: scanOcr.fileName,
                                via: "pdf_scan_ocr",
                              })
                            }
                            className="rounded-lg bg-lime px-4 py-2 text-sm font-semibold text-on-lime disabled:opacity-40"
                          >
                            {importDevisTextMutation.isPending
                              ? t("techPortal.importing")
                              : t("techPortal.importScanConfirmBtn")}
                          </button>
                          <button
                            type="button"
                            onClick={() => setScanOcr(null)}
                            className="rounded-lg border border-neutral-700 px-4 py-2 text-sm"
                          >
                            {t("techPortal.importScanCancelBtn")}
                          </button>
                        </div>
                      </div>
                    )}
                    <p className="text-xs text-neutral-400">{t("techPortal.importPhotoHint")}</p>
                  </div>
                )}
              </section>
            )}

            {dossiers.map((d) => {
              const claimed = d.claimedLines;
              const dspClaimed = claimed.some((l) => l.serviceType === "dsp");
              const montClaimed = claimed.some((l) => l.serviceType === "montage_demontage");
              const busy = claimMutation.isPending;
              // F-carro-pronto (CEO 2026-09-20) — carro com TUDO feito não deve
              // abrir a tela de "como você vai reparar": quem pesquisa a placa
              // precisa ver, de cara, que não há mais nada a fazer nele. O devis
              // e as fotos continuam à vista, que é o que serve de consulta.
              //
              // CEO 2026-09-23 (caso FS-374-MW): a regra vem do BACKEND
              // (`statusDoCarro`, a mesma do quadro do pátio e do portal do
              // gerente). A cópia daqui exigia as duas metades da montagem de
              // QUALQUER dono — e o técnico PDR que assumia PDR+Montagem não tinha
              // botão nenhum para marcá-las: o carro entrava no alarme "a
              // finalizar" e o card continuava perguntando "como você vai
              // reparar?", sem deixar concluir.
              const carroPronto =
                d.operationStatus === "reparado" || d.operationStatus === "entregue";
              // F-recepcao-so-senior — quem não recebe carro só assume depois da recepção completa.
              const podeAssumir = d.canReceiveVehicle || d.receptionComplete;
              // F-derrogacao-portal (CEO 2026-09-28) — o servidor aceita o claim
              // quando a trava está satisfeita (inclui a DERROGAÇÃO do super
              // admin); a tela exigia a recepção gravada e deixava o botão apagado.
              const recepcaoOk = d.receptionDone || d.receptionComplete;
              return (
                <div
                  key={d.id}
                  className={`rounded-xl border bg-neutral-900 p-4 space-y-3 ${
                    carroPronto ? "border-lime/40" : "border-neutral-800"
                  }`}
                >
                  {/* CEO 2026-10-19 — carro fechado (entregue) sem NENHUMA linha:
                      não é "já reparado", é carro sem técnico lançado. */}
                  {carroPronto && d.hasAnyLine === false ? (
                    <div
                      className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2"
                      data-testid="car-closed-no-tech"
                    >
                      <p className="text-sm font-semibold text-amber-300">
                        ⚠ {t("techPortal.carClosedNoTechTitle")}
                      </p>
                      <p className="text-xs text-neutral-300">
                        {t("techPortal.carClosedNoTechHint")}
                      </p>
                    </div>
                  ) : carroPronto ? (
                    <div className="rounded-lg border border-lime/40 bg-lime/10 px-3 py-2">
                      <p className="text-sm font-semibold text-lime">
                        ✓ {t("techPortal.carRepairedTitle")}
                      </p>
                      <p className="text-xs text-neutral-300">{t("techPortal.carRepairedHint")}</p>
                    </div>
                  ) : null}
                  {/* F-layout-card (CEO 2026-09-21: "melhore a apresentação,
                  está bagunçado") — a comissão ficava numa coluna à direita e,
                  no celular, "Votre commission (PDR)" quebrava em quatro linhas
                  espremendo a placa. Agora identificação em cima, em largura
                  inteira, e as comissões numa faixa própria embaixo. */}
                  <div className="space-y-2">
                    <div className="relative space-y-0.5">
                      {/* CEO 2026-09-23 (Volodymyr): abriu o carro, não quis
                      assumir agora e não tinha como SAIR dele para buscar
                      outro — o único ✕ era o do campo da placa. Fecha o card
                      e limpa a busca; o carro continua lá na próxima busca. */}
                      <button
                        type="button"
                        aria-label="close-car"
                        onClick={() => {
                          setSearched(null);
                          setPlate("");
                          setVin("");
                        }}
                        className="absolute right-0 top-0 rounded-md border border-neutral-700 px-2 py-0.5 text-sm text-neutral-400 hover:border-red-400 hover:text-red-300"
                      >
                        ✕
                      </button>
                      <p className="font-mono text-lg font-bold tracking-wider">{d.plate}</p>
                      <p className="flex items-center gap-1.5 text-sm text-neutral-300">
                        <CarLogo make={d.vehicleMakeSnapshot} size={26} />
                        {d.vehicleMakeSnapshot ?? "—"}
                      </p>
                      <p className="text-xs text-neutral-400">
                        {d.customerName} · {d.externalDevisRef} · {d.devisDate ?? ""}
                      </p>
                      {/* F-tech-location — onde é o carro: chantier e/ou endereço
                      do cadastro do cliente (distingue CHIR-AUTO Bellevue ×
                      Carnot, que são cadastros distintos). */}
                      {/* F-chk-lines (CEO 2026-09-19) — VALOR HT do devis visível
                      ao técnico (decisão de hoje; antes oculto). */}
                      {d.devisTotalHt != null && (
                        <p className="text-xs text-neutral-400">
                          {t("techPortal.devisHtLabel")}:{" "}
                          <span className="font-mono font-semibold text-neutral-200">
                            {formatMoney(d.devisTotalHt, { decimals: 2 })}
                          </span>
                          {d.devisFranchiseDeducted && (
                            <span className="ml-1 text-neutral-400">
                              · {t("techPortal.devisNetOfFranchise")}
                            </span>
                          )}
                        </p>
                      )}
                      {/* F-montador — o montador vê SÓ o valor da montagem. */}
                      {d.montageTotalHt != null && (
                        <p className="text-xs text-neutral-400">
                          {t("techPortal.montageHtLabel")}:{" "}
                          <span className="font-mono font-semibold text-neutral-200">
                            {formatMoney(d.montageTotalHt, { decimals: 2 })}
                          </span>
                          {d.devisFranchiseDeducted && (
                            <span className="ml-1 text-neutral-400">
                              · {t("techPortal.devisNetOfFranchise")}
                            </span>
                          )}
                        </p>
                      )}
                      {/* F-atelier-late — carro importado antes do cadastro dos
                      ateliês: o técnico define aqui onde ele está. */}
                      {d.atelierName == null && (d.customerAteliers ?? []).length > 0 && (
                        <select
                          value=""
                          disabled={setAtelierMutation.isPending}
                          onChange={(e) => {
                            const id = Number(e.target.value);
                            if (id > 0)
                              setAtelierMutation.mutate({
                                companyId: effectiveCompanyId!,
                                dossierId: d.id,
                                atelierId: id,
                              });
                          }}
                          className="mt-0.5 rounded-md border border-amber-500/50 bg-neutral-950 px-2 py-1 text-xs text-amber-200"
                        >
                          <option value="">📍 {t("techPortal.importAtelierPlaceholder")}</option>
                          {d.customerAteliers.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.name}
                            </option>
                          ))}
                        </select>
                      )}
                      {(d.customerLocation || d.chantierName) && (
                        <p className="text-xs text-neutral-400">
                          📍{" "}
                          {[
                            d.atelierName,
                            d.chantierName
                              ? [d.chantierName, d.chantierCity].filter(Boolean).join(" — ")
                              : null,
                            d.customerLocation,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                      )}
                    </div>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {d.hasDsp && !isMontador && (
                        <div className="rounded-lg border border-neutral-800 bg-neutral-950/60 px-2.5 py-1.5">
                          <p className="text-xs uppercase tracking-wider text-neutral-400">
                            {t("techPortal.myCommissionPdr")}
                          </p>
                          <p className="font-mono text-base font-bold">
                            {d.myCommission.pdr != null
                              ? formatMoney(d.myCommission.pdr, { decimals: 2 })
                              : t("techPortal.commissionTbd")}
                          </p>
                        </div>
                      )}
                      {/* F-comissao-fases (CEO 2026-09-20) — o campo mostrava a
                      comissão CHEIA do dégarnissage mesmo quem só tivesse
                      desmontado. Agora cada metade aparece com o seu valor:
                      a concluída em lima, a que falta em fosco. Antes de
                      assumir, segue mostrando o total como estimativa. */}
                      {d.hasMontage &&
                        (() => {
                          // F-metade-dono — a linha pode ser de outro e a metade
                          // ser minha (ou o contrário). Só as MINHAS metades entram.
                          const minha = d.claimedLines.find(
                            (l) =>
                              l.serviceType === "montage_demontage" &&
                              (l.demontageMine || l.montageMine),
                          );
                          const meio = d.myCommission.montagePerPhase;
                          if (minha == null || meio == null) {
                            // Montagem de OUTRO técnico: o que sobra para mim é a
                            // METADE em aberto, não o dégarnissage inteiro.
                            const outroTem = montClaimed && minha == null;
                            const valor = outroTem && meio != null ? meio : d.myCommission.montage;
                            return (
                              <div className="rounded-lg border border-neutral-800 bg-neutral-950/60 px-2.5 py-1.5">
                                <p className="text-xs uppercase tracking-wider text-neutral-400">
                                  {t("techPortal.myCommissionMontage")}
                                  {outroTem &&
                                    meio != null &&
                                    ` · ${t("techPortal.commissionMontageHalf")}`}
                                </p>
                                <p className="font-mono text-base font-bold">
                                  {valor != null
                                    ? formatMoney(valor, { decimals: 2 })
                                    : t("techPortal.commissionTbd")}
                                </p>
                              </div>
                            );
                          }
                          const metades = [
                            ...(minha.demontageMine
                              ? [
                                  {
                                    rotulo: t("techPortal.commissionDemontage"),
                                    feita: minha.demontageDone,
                                  },
                                ]
                              : []),
                            ...(minha.montageMine
                              ? [
                                  {
                                    rotulo: t("techPortal.commissionMontageHalf"),
                                    feita: minha.montageDone,
                                  },
                                ]
                              : []),
                          ];
                          return (
                            <div className="space-y-0.5 rounded-lg border border-neutral-800 bg-neutral-950/60 px-2.5 py-1.5">
                              {metades.map((m) => (
                                <p
                                  key={m.rotulo}
                                  className={
                                    m.feita
                                      ? "font-mono text-lime"
                                      : "font-mono text-neutral-400 line-through"
                                  }
                                >
                                  <span className="font-sans text-xs">{m.rotulo} </span>
                                  {formatMoney(meio, { decimals: 2 })}
                                  {!m.feita && (
                                    <span className="ml-1 font-sans text-xs no-underline">
                                      · {t("techPortal.commissionPendingHint")}
                                    </span>
                                  )}
                                </p>
                              ))}
                            </div>
                          );
                        })()}
                    </div>
                  </div>

                  {/* F-devis-panels — o que reparar: peça, método, impactos, D1/D2 */}
                  {d.panels.length > 0 && (
                    <div className="rounded-lg border border-neutral-800 bg-neutral-950/70 p-3 space-y-1.5">
                      <p className="text-xs uppercase tracking-wider text-neutral-400">
                        {t("techPortal.panelsTitle")}
                        {d.impactsTotal != null &&
                          ` · ${d.impactsTotal} ${t("techPortal.impactsWord")}`}
                      </p>
                      {d.panels.map((pl, i) => (
                        <div key={i} className="flex items-start justify-between gap-2 text-sm">
                          <span className="min-w-0">
                            {trPanel(pl.panel, locale)}
                            {pl.note && (
                              <span className="block text-xs text-amber-300">
                                ⚠ {trNote(pl.note, locale)}
                              </span>
                            )}
                          </span>
                          <span className="flex shrink-0 items-center gap-1.5">
                            {pl.impacts != null && (
                              <span className="font-mono text-xs text-neutral-400">
                                {pl.impacts}×
                              </span>
                            )}
                            {pl.sizeClass && (
                              <span className="rounded bg-neutral-800 px-1.5 py-0.5 text-xs text-neutral-300">
                                {pl.sizeClass}
                              </span>
                            )}
                            <span
                              className={`rounded px-1.5 py-0.5 text-xs font-semibold ${
                                pl.method.toUpperCase() === "DSP"
                                  ? "bg-lime/15 text-lime"
                                  : pl.method.toUpperCase() === "DAP"
                                    ? "bg-amber-500/15 text-amber-300"
                                    : /ECHANGE|REMPLACEMENT/i.test(pl.method)
                                      ? "bg-red-500/15 text-red-300"
                                      : "bg-blue-500/15 text-blue-300"
                              }`}
                            >
                              {trMethod(pl.method, locale)}
                            </span>
                          </span>
                        </div>
                      ))}
                      {d.deposeRepose && (
                        <p className="border-t border-neutral-800 pt-1.5 text-xs text-neutral-400">
                          {t("techPortal.deposeReposeLabel")}: {trDepose(d.deposeRepose, locale)}
                        </p>
                      )}
                    </div>
                  )}

                  {claimed.length > 0 && (
                    <p className="text-xs text-amber-300 bg-amber-500/5 border border-amber-500/20 rounded px-2 py-1">
                      {claimed
                        .map(
                          (l) =>
                            `${l.serviceType === "dsp" ? "PDR" : t("techPortal.montageShort")}: ${l.technicianName}`,
                        )
                        .join(" · ")}
                    </p>
                  )}

                  {/* F-portal-reception-gate — o carro só libera para reparo após
                  a recepção (franquia + check-list). F-recepcao-so-senior (CEO
                  2026-09-24): a recepção é do GERENTE ou do técnico SÊNIOR;
                  montador, PDR comum e atelier só esperam. */}
                  {!recepcaoOk && !d.canReceiveVehicle ? (
                    <div
                      className="space-y-1 rounded-lg border p-3"
                      style={{ background: "#FEF9C3", borderColor: "#CA8A04", color: "#854D0E" }}
                      data-testid="reception-waiting"
                    >
                      <p className="text-sm font-bold">⏳ {t("techPortal.recWaitingTitle")}</p>
                      <p className="text-xs">{t("techPortal.recWaitingHint")}</p>
                    </div>
                  ) : !d.receptionDone &&
                    !d.canReceiveVehicle ? null /* liberado por derrogação */ : !d.receptionDone ? (
                    <div
                      className="space-y-2 rounded-lg border p-3"
                      style={{ background: "#FEF9C3", borderColor: "#CA8A04", color: "#854D0E" }}
                    >
                      <p className="text-sm font-bold">📋 {t("techPortal.recTitle")}</p>
                      <p className="text-xs">
                        {d.receptionPhotosRequired
                          ? t("techPortal.recHint")
                          : t("techPortal.recHintNoPhotos")}
                      </p>
                      {/* F-franquia-primeiro (CEO 2026-09-29, GT-961-BG: "o campo da
                      franquia fica meio escondido; a validação deve ser
                      evidente e obrigatória antes das fotos"). A pergunta vale
                      para TODO cliente (antes: só particular / PRO com o flag)
                      e é o passo 1 — as fotos só aparecem depois da resposta.
                      Quem não pode mexer na franquia (não sênior) não a vê. */}
                      {d.canEditFranchise && (
                        <div
                          className="space-y-2 rounded-lg border-2 p-3"
                          style={{ borderColor: "#CA8A04", background: "#fff" }}
                          data-testid="reception-franchise-step"
                        >
                          <p className="text-xs font-bold uppercase tracking-wider">
                            {t("techPortal.recFranchiseStep")}
                          </p>
                          <p className="text-sm font-semibold">{t("techPortal.recFranchiseQ")}</p>
                          <div className="flex gap-2">
                            {(["yes", "no"] as const).map((v) => (
                              <button
                                key={v}
                                onClick={() =>
                                  setRecDraft({
                                    ...(recDraft?.dossierId === d.id
                                      ? recDraft
                                      : {
                                          dossierId: d.id,
                                          amount: "",
                                          damages: "",
                                          chk: EMPTY_CHECKLIST_DRAFT,
                                        }),
                                    dossierId: d.id,
                                    franchise: v,
                                  })
                                }
                                className="rounded-md px-4 py-2 text-sm font-bold"
                                style={
                                  recDraft?.dossierId === d.id && recDraft.franchise === v
                                    ? { background: "#CA8A04", color: "#fff" }
                                    : {
                                        background: "#fff",
                                        color: "#854D0E",
                                        border: "1px solid #CA8A04",
                                      }
                                }
                              >
                                {v === "yes" ? t("techPortal.recYes") : t("techPortal.recNo")}
                              </button>
                            ))}
                          </div>
                          {recDraft?.dossierId === d.id && recDraft.franchise === "yes" && (
                            <input
                              type="number"
                              inputMode="decimal"
                              min={0}
                              step="0.01"
                              value={recDraft.amount}
                              onChange={(e) => setRecDraft({ ...recDraft, amount: e.target.value })}
                              placeholder={t("techPortal.recAmountPh")}
                              className="w-full rounded-md border px-3 py-2.5 text-sm"
                              style={{
                                borderColor: "#CA8A04",
                                background: "#fff",
                                color: "#1F2937",
                              }}
                            />
                          )}
                        </div>
                      )}
                      {/* F-recepcao-sem-fotos (CEO 2026-10-02) — fora do chantier
                      não há fotos, checklist nem danos: só a franquia. */}
                      {!d.receptionPhotosRequired ? null : d.canEditFranchise &&
                        (recDraft?.dossierId !== d.id || recDraft.franchise === "") ? (
                        <p className="text-xs font-semibold">
                          🔒 {t("techPortal.recFranchiseFirstHint")}
                        </p>
                      ) : (
                        <>
                          {d.canEditFranchise && (
                            <p className="text-xs font-bold uppercase tracking-wider">
                              {t("techPortal.recPhotosStep")}
                            </p>
                          )}
                          <input
                            value={recDraft?.dossierId === d.id ? recDraft.damages : ""}
                            onChange={(e) =>
                              setRecDraft({
                                ...(recDraft?.dossierId === d.id
                                  ? recDraft
                                  : {
                                      dossierId: d.id,
                                      franchise: "" as const,
                                      amount: "",
                                      chk: EMPTY_CHECKLIST_DRAFT,
                                    }),
                                dossierId: d.id,
                                damages: e.target.value,
                              })
                            }
                            placeholder={t("techPortal.recDamagesPh")}
                            className="w-full rounded-md border px-3 py-2.5 text-sm"
                            style={{ borderColor: "#CA8A04", background: "#fff", color: "#1F2937" }}
                          />
                          {/* F2 checklist — km + pára-brisa/ciel + fotos guiadas +
                      assinatura, já na recepção inicial. */}
                          <TechChecklistBlock
                            companyId={effectiveCompanyId!}
                            montador={isMontador}
                            showSignature={d.signatureRequired}
                            dossierId={d.id}
                            value={
                              recDraft?.dossierId === d.id ? recDraft.chk : EMPTY_CHECKLIST_DRAFT
                            }
                            onChange={(chk) =>
                              setRecDraft({
                                ...(recDraft?.dossierId === d.id
                                  ? recDraft
                                  : {
                                      dossierId: d.id,
                                      franchise: "" as const,
                                      amount: "",
                                      damages: "",
                                    }),
                                dossierId: d.id,
                                chk,
                              })
                            }
                            alreadySigned={false}
                          />
                        </>
                      )}
                      <button
                        onClick={() => {
                          if (recDraft?.dossierId !== d.id) return;
                          // Sem autorização de sênior a pergunta da franquia nem
                          // aparece: exigi-la aqui deixaria o técnico preso num
                          // botão eternamente desabilitado.
                          const pedeFranquia = d.canEditFranchise;
                          if (pedeFranquia && recDraft.franchise === "") return;
                          const fr = pedeFranquia && recDraft.franchise === "yes";
                          const chk = recDraft.chk;
                          saveReceptionMutation.mutate({
                            companyId: effectiveCompanyId!,
                            dossierId: d.id,
                            franchise: fr,
                            franchiseAmountTtc: fr ? Number(recDraft.amount) : null,
                            damages: recDraft.damages
                              .split(",")
                              .map((s) => s.trim())
                              .filter(Boolean),
                            mileageKm:
                              chk.mileage.trim() !== "" ? Math.round(Number(chk.mileage)) : null,
                            pareBriseOk: chk.pareBrise === "" ? null : chk.pareBrise === "ok",
                            cielOk: chk.ciel === "" ? null : chk.ciel === "ok",
                            signatureBase64: chk.signature,
                            signedByName: chk.signedName.trim() || null,
                          });
                        }}
                        disabled={
                          saveReceptionMutation.isPending ||
                          recDraft?.dossierId !== d.id ||
                          (d.canEditFranchise && recDraft.franchise === "") ||
                          (d.canEditFranchise &&
                            recDraft.franchise === "yes" &&
                            (!recDraft.amount || Number(recDraft.amount) <= 0))
                        }
                        className="w-full rounded-lg px-4 py-3 text-sm font-bold disabled:opacity-40"
                        style={{ background: "#CA8A04", color: "#fff" }}
                      >
                        {saveReceptionMutation.isPending
                          ? t("techPortal.recSaving")
                          : `✔ ${t("techPortal.recSaveBtn")}`}
                      </button>
                      {/* O erro precisa aparecer ONDE se aperta o botão: o aviso
                      do topo da página fica fora da tela no celular, e o
                      técnico lê isso como "o app não prossegue". */}
                      {saveReceptionMutation.isError && recDraft?.dossierId === d.id && (
                        <p className="rounded-md bg-red-100 px-3 py-2 text-xs font-semibold text-red-700">
                          ✕ {saveReceptionMutation.error.message}
                        </p>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between gap-2 rounded-md border border-neutral-800 bg-neutral-900/60 px-3 py-2 text-xs text-neutral-300">
                        <span>
                          📥 {t("techPortal.recDoneLabel")} ·{" "}
                          {d.receptionFranchise
                            ? `${t("techPortal.recFranchisePre")} ${
                                d.receptionFranchiseAmountTtc != null
                                  ? formatMoney(d.receptionFranchiseAmountTtc, { decimals: 2 })
                                  : ""
                              } TTC`
                            : t("techPortal.recNoFranchise")}
                        </span>
                        {/* F-franchise-late (CEO 2026-09-19) — a franquia pode ser
                        inserida/corrigida TARDIAMENTE, mesmo após reparado
                        (cliente esqueceu de informar no início). O recálculo
                        central é reversível e refaz as bases. */}
                        {/* Montador não edita franquia (CEO 2026-09-20) — é o PDR
                        ou o gerente do chantier quem responde por ela.
                        F-tecnico-senior (CEO 2026-09-21): e entre os PDR, só o
                        SÊNIOR autorizado. Os demais veem o valor (a linha ao
                        lado), mas não o botão — negociar desconto com o
                        cliente não é de todo mundo. */}
                        {d.canEditFranchise && (
                          <button
                            onClick={() =>
                              setLateFranchiseId(lateFranchiseId === d.id ? null : d.id)
                            }
                            className="shrink-0 rounded border border-neutral-700 px-2 py-0.5 text-xs hover:border-lime"
                          >
                            ✎ {t("techPortal.chkFranchiseEditBtn")}
                          </button>
                        )}
                      </div>
                      {lateFranchiseId === d.id && (
                        <LateFranchiseEditor
                          companyId={effectiveCompanyId!}
                          dossierId={d.id}
                          initialYes={d.receptionFranchise}
                          initialAmount={d.receptionFranchiseAmountTtc}
                          saving={saveReceptionMutation.isPending}
                          onSave={(fr, amount) => {
                            saveReceptionMutation.mutate({
                              companyId: effectiveCompanyId!,
                              dossierId: d.id,
                              franchise: fr,
                              franchiseAmountTtc: fr ? amount : null,
                            });
                            setLateFranchiseId(null);
                          }}
                        />
                      )}
                      {/* F2 — recepção salva mas checklist incompleto: card para
                      completar (fotos/assinatura/km) sem refazer a franquia.
                      Carro JÁ REPARADO não pede mais nada: cobrar "complete
                      para liberar o reparo" num carro pronto é contradição.
                      CEO 2026-10-12 (Luciano Molina, fora de chantier): fora do
                      chantier a recepção é SÓ a franquia (F-recepcao-sem-fotos)
                      e o servidor não cobra foto nenhuma — este card pedia
                      "2 fotos + pára-brisa + ciel para liberar o reparo" num
                      carro que já estava liberado. Só no chantier. */}
                      {!carroPronto && d.canReceiveVehicle && d.receptionPhotosRequired && (
                        <TechChecklistCompleteCard
                          companyId={effectiveCompanyId!}
                          montador={isMontador}
                          signatureRequired={d.signatureRequired}
                          d={d}
                        />
                      )}
                      {/* F-recepcao-so-senior — recepção salva mas incompleta: quem
                      não recebe carro não completa nada, só espera. */}
                      {!carroPronto && !d.canReceiveVehicle && !d.receptionComplete && (
                        <p
                          className="rounded-md border px-3 py-2 text-xs"
                          style={{
                            background: "#FEF9C3",
                            borderColor: "#CA8A04",
                            color: "#854D0E",
                          }}
                          data-testid="reception-incomplete-waiting"
                        >
                          ⏳ {t("techPortal.recIncompleteWait")}
                        </p>
                      )}
                    </>
                  )}

                  {/* Fotos e marcação das DUAS METADES do dégarnissage (desmontar
                  / montar). CEO 2026-09-23: não é mais exclusivo do montador —
                  quem assume a linha de montagem marca as metades, seja ele
                  montador ou técnico PDR que pegou "PDR + Montagem". O backend
                  já autorizava pelo DONO DA FASE, só a tela escondia. */}
                  {/* F-pdr-fim — o dono do PDR marca o fim do serviço. */}
                  {claimed.some((l) => l.serviceType === "dsp" && l.mine && !l.dspDone) && (
                    <div className="flex items-center justify-between gap-2 rounded-md border border-lime/30 bg-lime/5 px-3 py-2">
                      <span className="text-xs font-semibold text-neutral-200">
                        {t("techPortal.pdrDoneQ")}
                      </span>
                      <button
                        type="button"
                        disabled={dspDoneMut.isPending}
                        onClick={() =>
                          dspDoneMut.mutate({ companyId: effectiveCompanyId!, dossierId: d.id })
                        }
                        className="shrink-0 rounded-md bg-lime px-3 py-1.5 text-xs font-semibold text-on-lime disabled:opacity-40"
                      >
                        {t("techPortal.pdrDoneBtn")}
                      </button>
                    </div>
                  )}
                  {claimed.some((l) => l.serviceType === "dsp" && l.mine && l.dspDone) && (
                    <p className="rounded-md border border-lime/30 bg-lime/5 px-3 py-2 text-xs font-semibold text-lime">
                      ✓ {t("techPortal.toastPdrDone")}
                    </p>
                  )}
                  {(isMontador || montClaimed) && (
                    <MontadorPhasesBlock
                      companyId={effectiveCompanyId!}
                      dossierId={d.id}
                      onConcluded={concluiuFase}
                      montadorName={
                        claimed.find((l) => l.serviceType === "montage_demontage")
                          ?.technicianName ?? null
                      }
                      demontageMine={claimed.some(
                        (l) => l.serviceType === "montage_demontage" && l.demontageMine,
                      )}
                      montageMine={claimed.some(
                        (l) => l.serviceType === "montage_demontage" && l.montageMine,
                      )}
                      demontageDone={claimed.some(
                        (l) => l.serviceType === "montage_demontage" && l.demontageDone,
                      )}
                      montageDone={claimed.some(
                        (l) => l.serviceType === "montage_demontage" && l.montageDone,
                      )}
                      montageTaken={claimed.some(
                        (l) => l.serviceType === "montage_demontage" && l.montageTaken,
                      )}
                      montageExternal={claimed.some(
                        (l) => l.serviceType === "montage_demontage" && l.montageExternal,
                      )}
                      perPhase={d.myCommission.montagePerPhase ?? null}
                    />
                  )}
                  {/* CEO 2026-10-10 (FJ-929-KH, Luciano): documentos da recepção
                  (rapport d'expertise, carte grise, permis) e a conferência do
                  rapport são SÓ do chefe de ateliê — portal do gerente de
                  chantier. Nenhum técnico vê isto, nem o sênior. Fora de
                  chantier (ateliê) `canReceiveVehicle` era true para todos, e o
                  formulário aparecia para qualquer um. */}
                  {/* F-chk-lines — ÚLTIMA linha do checklist: como vai reparar. */}
                  {!carroPronto && podeAssumir && (
                    <p className="pt-1 text-xs font-semibold text-neutral-400">
                      🔧 {t("techPortal.chkClaimQ")}
                    </p>
                  )}
                  {/* Carro já concluído não mostra mais "assumir": os botões ficavam
                  apagados no rodapé do card, e o CEO leu isso como o portal
                  travando a conclusão (caso FS-374-MW). */}
                  <div
                    className={`grid grid-cols-1 gap-2${carroPronto || !podeAssumir ? " hidden" : ""}`}
                  >
                    {!isMontador && (
                      <>
                        <button
                          onClick={() =>
                            liberadoParaNovoCarro() &&
                            claimMutation.mutate({
                              companyId: effectiveCompanyId!,
                              dossierId: d.id,
                              choice: "pdr_montage",
                            })
                          }
                          disabled={
                            busy ||
                            !recepcaoOk ||
                            dspClaimed ||
                            !d.hasDsp ||
                            !d.hasMontage ||
                            montClaimed
                          }
                          className="rounded-lg bg-lime text-on-lime px-4 py-3 text-sm font-semibold disabled:opacity-30"
                        >
                          {t("techPortal.claimPdrMontage")}
                        </button>
                        <button
                          onClick={() =>
                            liberadoParaNovoCarro() &&
                            claimMutation.mutate({
                              companyId: effectiveCompanyId!,
                              dossierId: d.id,
                              choice: "pdr",
                            })
                          }
                          disabled={busy || !recepcaoOk || dspClaimed || !d.hasDsp}
                          className="rounded-lg border border-lime/50 text-lime px-4 py-3 text-sm font-semibold disabled:opacity-30"
                        >
                          {t("techPortal.claimPdr")}
                        </button>
                        {/* CEO 2026-10-11 (Volodymyr = PDR + montador): o técnico
                        PDR também pode assumir SÓ o dégarnissage — a API já
                        permitia (F-papeis), faltava o botão. */}
                        {d.hasMontage && !montClaimed && (
                          <button
                            onClick={() =>
                              claimMutation.mutate({
                                companyId: effectiveCompanyId!,
                                dossierId: d.id,
                                choice: "montage",
                              })
                            }
                            disabled={busy || !recepcaoOk}
                            className="rounded-lg border border-neutral-600 px-4 py-3 text-sm font-semibold text-neutral-200 disabled:opacity-30"
                            data-testid="claim-montage-only"
                          >
                            {t("techPortal.claimMontageOnly")}
                          </button>
                        )}
                        {/* CEO 2026-10-12: o garage desmontou/montou e ninguém
                        lançou — o técnico do PDR declara aqui. */}
                        {d.hasMontage &&
                          !montClaimed &&
                          claimed.some((l) => l.serviceType === "dsp" && l.mine) && (
                            <button
                              type="button"
                              onClick={() => {
                                if (!window.confirm(t("techPortal.degarnissageGarageConfirm")))
                                  return;
                                degGarageMut.mutate({
                                  companyId: effectiveCompanyId!,
                                  dossierId: d.id,
                                });
                              }}
                              disabled={busy || degGarageMut.isPending}
                              className="rounded-lg border border-amber-500/50 px-4 py-3 text-sm font-semibold text-amber-200 disabled:opacity-30"
                              data-testid="degarnissage-garage"
                            >
                              {t("techPortal.degarnissageGarageBtn")}
                            </button>
                          )}
                      </>
                    )}
                    {/* F-montador-sem-montagem (CEO 2026-09-20) — devis sem
                    dégarnissage não tem nada para o montador: diz isso, em vez
                    de deixar um botão apagado sem explicação. */}
                    {isMontador && !d.hasMontage && (
                      <p className="rounded-lg border border-neutral-800 bg-neutral-950 px-4 py-3 text-sm text-neutral-400">
                        {t("techPortal.claimNoMontage")}
                      </p>
                    )}
                    {/* Já assumiu? O botão morto só confundia — o CEO clicava nele
                    achando que era ali que se marcava a fase. */}
                    {isMontador && d.hasMontage && !montClaimed && (
                      <p className="text-xs text-neutral-400">
                        <span className="font-semibold text-neutral-200">
                          {t("techPortal.claimDemontageQ")}
                        </span>{" "}
                        {t("techPortal.claimDemontageHint")}
                      </p>
                    )}
                    {isMontador && d.hasMontage && !montClaimed && (
                      <button
                        onClick={() =>
                          claimMutation.mutate({
                            companyId: effectiveCompanyId!,
                            dossierId: d.id,
                            choice: "montage",
                          })
                        }
                        disabled={busy || !recepcaoOk}
                        className="rounded-lg bg-lime text-on-lime px-4 py-3 text-sm font-semibold disabled:opacity-30"
                      >
                        {t("techPortal.claimMontage")}
                      </button>
                    )}
                    {/* CEO 2026-09-23 (Volodymyr no FG-284-SR): dizia "você já
                    assumiu" quando a montagem era de OUTRO montador. Só vale
                    para a linha que é minha. */}
                    {isMontador &&
                      claimed.some((l) => l.serviceType === "montage_demontage" && l.mine) && (
                        <p className="rounded-lg border border-lime/30 bg-lime/5 px-4 py-2.5 text-sm text-lime">
                          ✓ {t("techPortal.claimedAlready")}
                        </p>
                      )}
                    {/* O erro do "assumir" também precisa aparecer AQUI: no topo
                    da página ele fica fora da tela no celular e o técnico lê
                    como "o botão não faz nada". */}
                    {claimMutation.isError && (
                      <p className="rounded-lg bg-red-100 px-3 py-2 text-xs font-semibold text-red-700">
                        ✕ {claimMutation.error.message}
                      </p>
                    )}
                    {/* Recepção pendente é o motivo nº 1 de o botão ficar apagado
                    — dizer isso evita o técnico ficar tocando num botão
                    desabilitado sem entender. */}
                    {!recepcaoOk && !carroPronto && (
                      <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                        ⚠ {t("techPortal.claimNeedsReception")}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </>
        )}

        {/* Meus carros */}
        <section id="mes-carros" className="pt-2">
          <div className="mb-2 space-y-2">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-neutral-300">
              {t("techPortal.myCarsTitle")} ·{" "}
              {claimsPeriod === "all" ? t("techPortal.filterAll") : periodoLabel}
            </h2>
            <div className="grid grid-cols-2 gap-1.5">
              <select
                value={claimsPeriod}
                onChange={(e) => setClaimsPeriod(e.target.value as typeof claimsPeriod)}
                aria-label={t("techPortal.periodAria")}
                data-testid="mycars-period"
                className="rounded-md border border-neutral-700 bg-neutral-900 px-2 py-1.5 text-xs text-neutral-100"
              >
                <option value="since">{t("techPortal.filterSince")}</option>
                <option value="thisWeek">{t("techPortal.filterThisWeek")}</option>
                <option value="week">{t("techPortal.filterWeek")}</option>
                <option value="month">{t("techPortal.filterMonth")}</option>
                <option value="all">{t("techPortal.filterAll")}</option>
                <option value="range">{t("techPortal.filterRange")}</option>
              </select>
              <select
                value={claimsStatus}
                onChange={(e) => setClaimsStatus(e.target.value as typeof claimsStatus)}
                aria-label={t("techPortal.statusAria")}
                data-testid="mycars-status"
                className="rounded-md border border-neutral-700 bg-neutral-900 px-2 py-1.5 text-xs text-neutral-100"
              >
                <option value="all">{t("techPortal.filterAll")}</option>
                <option value="pending">{t("techPortal.statusPending")}</option>
                <option value="paid">{t("techPortal.statusPaid")}</option>
              </select>
            </div>
          </div>
          {claimsPeriod === "range" && (
            <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-neutral-400">
              <input
                type="date"
                value={claimsFrom}
                max={claimsTo}
                onChange={(e) => setClaimsFrom(e.target.value)}
                className="rounded-md border border-neutral-700 bg-neutral-950 px-2 py-1 text-neutral-100"
                data-testid="claims-from"
              />
              <span>→</span>
              <input
                type="date"
                value={claimsTo}
                min={claimsFrom}
                onChange={(e) => setClaimsTo(e.target.value)}
                className="rounded-md border border-neutral-700 bg-neutral-950 px-2 py-1 text-neutral-100"
                data-testid="claims-to"
              />
            </div>
          )}

          {/* F-producao (CEO 2026-10-06) — soma da produção do técnico no
              período escolhido acima (7 dias / 30 dias / total), com o que já
              foi pago e o que falta receber. */}
          {myClaims.length > 0 &&
            (() => {
              let total = 0;
              let pago = 0;
              let aCalcular = false;
              for (const c of myClaims) {
                const v = c.commissionAmount ?? 0;
                total += v;
                // CEO 2026-10-12: pago POR PARTE (PDR pago + metade da montagem
                // a receber = carro conta nos dois) — antes era tudo-ou-nada.
                pago += c.paidAmount ?? (c.received ? v : 0);
                if (c.commissionAmount == null || c.pendingCalc) aCalcular = true;
              }
              const periodo = periodoLabel;
              return (
                <div
                  className="mb-3 rounded-lg border border-lime/30 bg-lime/5 px-4 py-3"
                  data-testid="tech-production"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-xs uppercase tracking-wider text-neutral-400">
                      {t("techPortal.prodTitle")} · {periodo}
                    </span>
                    <span className="text-xs text-neutral-400">
                      {myClaims.length}{" "}
                      {myClaims.length === 1 ? t("techPortal.prodCar") : t("techPortal.prodCars")}
                    </span>
                  </div>
                  <p className="mt-1 font-mono text-2xl font-bold text-lime">
                    {formatMoney(total, { decimals: 2 })}
                  </p>
                  <p className="mt-1 text-xs text-neutral-400">
                    💶 {t("techPortal.prodPaid")}{" "}
                    <span className="font-mono text-neutral-200">
                      {formatMoney(pago, { decimals: 2 })}
                    </span>
                    {" · "}
                    {t("techPortal.prodToReceive")}{" "}
                    <span className="font-mono text-neutral-200">
                      {formatMoney(total - pago, { decimals: 2 })}
                    </span>
                  </p>
                  {aCalcular && (
                    <p className="mt-1 text-xs text-neutral-400">+ {t("techPortal.prodTbdNote")}</p>
                  )}
                </div>
              );
            })()}

          {/* F-adiantamento-tecnico (CEO 2026-10-25) — o técnico vê o que a
              empresa adiantou (hotel, ferramentas…) e deve descontar na
              próxima fatura; depois, o histórico recente "descontado em". */}
          <TechAdvancesBlock />

          {/* F-worklog — o TÉCNICO baixa o próprio worklog da semana (o
              mesmo PDF cliente-safe, por cliente). */}
          <div id="worklog">
            {effectiveCompanyId != null && <TechWorklogBlock companyId={effectiveCompanyId} />}
          </div>
          {myClaims.length === 0 && (
            <p className="text-sm text-neutral-400">{t("techPortal.myCarsEmpty")}</p>
          )}
          <div className="space-y-2">
            {myClaims.map((c) => (
              <div
                key={c.dossierId}
                className="rounded-lg border border-neutral-800 bg-neutral-900/60 text-sm"
              >
                {/* F-portal-claim-devis — linha inteira é botão: abre o devis. */}
                <button
                  onClick={() =>
                    setOpenClaimId(
                      openClaimId?.dossierId === c.dossierId
                        ? null
                        : { dossierId: c.dossierId, companyId: c.companyId },
                    )
                  }
                  className="grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-x-2 px-3 py-2 text-left"
                  data-testid="mycars-row"
                >
                  {/* CEO 2026-10-12 (print): a data ficava atrás do modelo
                      truncado e os chips desalinhavam a linha. Agora: 3 colunas
                      fixas (logo · texto · valor); na 1ª linha a DATA é
                      `shrink-0` e quem trunca é o modelo; os chips têm linha
                      própria; o valor nunca quebra. */}
                  <CarLogo make={c.vehicleMakeSnapshot} size={22} />
                  <span className="min-w-0">
                    <span className="flex items-baseline gap-x-2">
                      <span className="shrink-0 font-mono font-semibold">
                        {c.plate ?? c.dossierCode}
                      </span>
                      {c.vehicleMakeSnapshot && (
                        <span className="min-w-0 truncate text-neutral-400">
                          {c.vehicleMakeSnapshot}
                        </span>
                      )}
                      <span
                        className="ml-auto shrink-0 text-xs tabular-nums text-neutral-400"
                        data-testid="mycars-date"
                      >
                        {new Date(c.createdAt).toLocaleDateString("fr-FR", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "2-digit",
                        })}
                      </span>
                    </span>
                    <span className="flex items-center gap-1 text-xs uppercase text-neutral-400">
                      <span className="min-w-0 truncate">{c.customerName}</span>
                      {/* F-portal-history-all — empresa do carro (histórico
                          pode incluir empresa onde ele não atua mais). */}
                      {multiEmpresa && (
                        <span
                          className="shrink-0 rounded bg-neutral-800 px-1 py-0.5 text-xs normal-case text-neutral-400"
                          title={c.companyName}
                        >
                          {siglaEmpresa(c.companyName)}
                        </span>
                      )}
                    </span>
                    {/* F-portal-consulta — a conta junta mais de um cadastro
                          (ex.: Jhonatas + a dupla): diz de quem é o carro. */}
                    {somenteConsulta && (
                      <span className="block truncate text-xs text-sky-300">
                        👤{" "}
                        {[
                          ...new Set(
                            c.services.filter((sv) => sv.mine).map((sv) => sv.technicianName),
                          ),
                        ].join(" · ")}
                      </span>
                    )}
                    <span className="mt-1 flex flex-wrap items-center gap-1">
                      {c.serviceTypes.map((s) => (
                        <span
                          key={s}
                          className="rounded bg-neutral-800 px-1.5 py-0.5 text-xs text-neutral-300"
                        >
                          {s === "dsp" ? "PDR" : t("techPortal.montageShort")}
                        </span>
                      ))}
                      {/* F-mycars-fase (CEO 2026-09-21) — "o carro deve ir
                          para a lista do desmontador como desmontado". A
                          etapa do dégarnissage já aparecia ao expandir; na
                          linha, que é o que ele bate o olho, não. */}
                      {(() => {
                        const m = c.services.find(
                          (sv) => sv.serviceType === "montage_demontage" && sv.mine,
                        );
                        if (!m) return null;
                        if (m.demontageDone && m.montageDone) {
                          return (
                            <span className="rounded bg-lime/15 px-1.5 py-0.5 text-xs text-lime">
                              ✓ {t("techPortal.rowMontageDone")}
                            </span>
                          );
                        }
                        if (m.demontageDone) {
                          return (
                            <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-xs text-amber-300">
                              ✓ {t("techPortal.rowDemontageDone")}
                            </span>
                          );
                        }
                        return null;
                      })()}
                      {/* F-portal-busca-primeiro — a SITUAÇÃO do carro (em
                          reparação, montagem a fazer, terminado, pago…) na
                          linha; antes só aparecia o dinheiro. */}
                      {(() => {
                        const sit = situacaoPortal(c);
                        return (
                          <span
                            className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-semibold ${TONE_SIT[sit.tone]}`}
                            data-testid={sit.testid}
                            title={
                              sit.testid === "mycars-paid-partial"
                                ? `${t("techPortal.prodPaid")} ${formatMoney(c.paidAmount, { decimals: 2 })} · ${t("techPortal.prodToReceive")} ${formatMoney(c.pendingAmount ?? 0, { decimals: 2 })}`
                                : undefined
                            }
                          >
                            {sit.icone && <span aria-hidden="true">{sit.icone}</span>}
                            {sit.label}
                          </span>
                        );
                      })()}
                    </span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span className="flex items-baseline justify-end gap-1.5 whitespace-nowrap font-mono text-neutral-200">
                      {c.commissionAmount != null ? (
                        formatMoney(c.commissionAmount, { decimals: 2 })
                      ) : (
                        <span className="rounded bg-neutral-800 px-1.5 py-0.5 font-sans text-xs text-neutral-400">
                          {t("techPortal.commissionTbd")}
                        </span>
                      )}
                      <span className="font-sans text-neutral-400">
                        {openClaimId?.dossierId === c.dossierId ? "▴" : "▾"}
                      </span>
                    </span>
                    {c.commissionAmount != null && c.pendingCalc && (
                      <span className="mt-0.5 inline-block rounded bg-neutral-800 px-1.5 py-0.5 text-xs text-neutral-400">
                        + {t("techPortal.commissionTbd")}
                      </span>
                    )}
                  </span>
                </button>
                {/* F-portal-busca-primeiro — a próxima ação no próprio cartão
                    ("Marquer PDR terminé" / "dépose-repose"); antes só dentro
                    do detalhe aberto. */}
                {!somenteConsulta &&
                  (() => {
                    const sit = situacaoPortal(c);
                    if (sit.acao === "pdr") {
                      return (
                        <button
                          type="button"
                          disabled={dspDoneMut.isPending}
                          onClick={() =>
                            dspDoneMut.mutate({ companyId: c.companyId, dossierId: c.dossierId })
                          }
                          className="mx-3 mb-2.5 block w-[calc(100%-1.5rem)] rounded-md bg-lime px-3 py-2 text-sm font-semibold text-on-lime disabled:opacity-40"
                          data-testid="mycars-pdr-done"
                        >
                          ✓ {t("techPortal.pdrDoneBtn")}
                        </button>
                      );
                    }
                    if (
                      sit.acao === "phases" &&
                      (c.plate ?? c.vin) &&
                      companies.some((co) => co.companyId === c.companyId)
                    ) {
                      return (
                        <button
                          type="button"
                          onClick={() => abrirFases(c)}
                          className="mx-3 mb-2.5 block w-[calc(100%-1.5rem)] rounded-md border border-lime/50 bg-lime/10 px-3 py-2 text-sm font-semibold text-lime"
                          data-testid="mycars-open-phases"
                        >
                          🔧 {t("techPortal.mycarsMarkPhasesBtn")}
                        </button>
                      );
                    }
                    return null;
                  })()}
                {openClaimId?.dossierId === c.dossierId && (
                  <div className="border-t border-neutral-800 px-3 py-2">
                    {/* F-mycars-detail — mesmos níveis de informação do card:
                        comissão por serviço + franquia da recepção. */}
                    <div className="mb-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                      {!c.received && (c.paidAmount ?? 0) > 0 && (
                        <span className="text-amber-300">
                          💶 {t("techPortal.prodPaid")}{" "}
                          <span className="font-mono font-semibold">
                            {formatMoney(c.paidAmount, { decimals: 2 })}
                          </span>
                          {" · "}
                          {t("techPortal.prodToReceive")}{" "}
                          <span className="font-mono font-semibold">
                            {formatMoney(c.pendingAmount ?? 0, { decimals: 2 })}
                          </span>
                        </span>
                      )}
                      {c.commissionPdr != null && (
                        <span className="text-neutral-300">
                          {t("techPortal.myCommissionPdr")}:{" "}
                          <span className="font-mono font-semibold text-lime">
                            {formatMoney(c.commissionPdr, { decimals: 2 })}
                          </span>
                        </span>
                      )}
                      {c.commissionMontage != null && (
                        <span className="text-neutral-300">
                          {t("techPortal.myCommissionMontage")}:{" "}
                          <span className="font-mono font-semibold text-lime">
                            {formatMoney(c.commissionMontage, { decimals: 2 })}
                          </span>
                        </span>
                      )}
                      {/* F-mycars-date (CEO 2026-09-19) — data da REPARAÇÃO
                          (quando o técnico assumiu), além da data do devis. */}
                      <span className="text-neutral-400">
                        🔧 {t("techPortal.repairedAtPre")}{" "}
                        {new Date(c.createdAt).toLocaleDateString("fr-FR")}
                      </span>
                      {/* F-atelier-late — ateliê do carro no Meus carros;
                          sem ateliê + cliente com opções → define AQUI. */}
                      {c.atelierName && (
                        <span className="text-neutral-400">📍 {c.atelierName}</span>
                      )}
                      {!somenteConsulta &&
                        c.atelierName == null &&
                        (c.customerAteliers ?? []).length > 0 && (
                          <select
                            value=""
                            disabled={setAtelierMutation.isPending}
                            onChange={(e) => {
                              const id = Number(e.target.value);
                              if (id > 0)
                                setAtelierMutation.mutate({
                                  companyId: c.companyId,
                                  dossierId: c.dossierId,
                                  atelierId: id,
                                });
                            }}
                            className="rounded border border-amber-500/50 bg-neutral-950 px-1.5 py-0.5 text-xs text-amber-200"
                          >
                            <option value="">📍 {t("techPortal.importAtelierPlaceholder")}</option>
                            {c.customerAteliers.map((a) => (
                              <option key={a.id} value={a.id}>
                                {a.name}
                              </option>
                            ))}
                          </select>
                        )}
                      {c.franchiseTtc != null && (
                        <span className="rounded bg-amber-500/15 px-1.5 py-0.5 font-semibold text-amber-300">
                          {t("techPortal.recFranchisePre")}{" "}
                          {formatMoney(c.franchiseTtc, { decimals: 2 })} TTC
                        </span>
                      )}
                    </div>
                    {/* F-mycars-servicos (CEO 2026-09-20) — quem fez o quê,
                        dentro do PRÓPRIO card. Antes era preciso abrir o carro
                        pela busca, o que criava um segundo card do mesmo
                        veículo. O valor do PDR NÃO aparece: quanto o colega
                        ganhou não é da conta dele. */}
                    {c.services.length > 0 && (
                      <div className="mb-1.5 space-y-0.5">
                        <p className="text-xs uppercase tracking-wider text-neutral-400">
                          {t("techPortal.servicesTitle")}
                        </p>
                        {c.services.map((sv) => (
                          <p key={sv.serviceType} className="text-xs text-neutral-300">
                            {sv.serviceType === "dsp" ? (
                              <>
                                <span className="text-neutral-400">
                                  {t("techPortal.servicePdrLabel")}:{" "}
                                </span>
                                {sv.technicianName}
                                <span className={sv.dspDone ? "text-lime" : "text-neutral-400"}>
                                  {" "}
                                  {sv.dspDone ? "✓" : "○"}
                                </span>
                              </>
                            ) : (
                              <>
                                <span className="text-neutral-400">
                                  {t("techPortal.commissionDemontage")}:{" "}
                                </span>
                                {sv.demontageBy ?? sv.technicianName}
                                <span
                                  className={sv.demontageDone ? "text-lime" : "text-neutral-400"}
                                >
                                  {" "}
                                  {sv.demontageDone ? "✓" : "○"}
                                </span>
                                <span className="block">
                                  <span className="text-neutral-400">
                                    {t("techPortal.commissionMontageHalf")}:{" "}
                                  </span>
                                  {sv.montageExternal
                                    ? t("techPortal.phaseExternalShort")
                                    : (sv.montageBy ?? sv.technicianName)}
                                  <span
                                    className={sv.montageDone ? "text-lime" : "text-neutral-400"}
                                  >
                                    {" "}
                                    {sv.montageDone ? "✓" : "○"}
                                  </span>
                                </span>
                              </>
                            )}
                          </p>
                        ))}
                        <button
                          type="button"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            setPhotosCarId(photosCarId === c.dossierId ? null : c.dossierId);
                          }}
                          className="mt-1 rounded-md border border-neutral-700 px-2 py-0.5 text-xs text-neutral-300 hover:border-lime hover:text-lime"
                        >
                          📷{" "}
                          {photosCarId === c.dossierId
                            ? t("techPortal.hidePhotosBtn")
                            : t("techPortal.showPhotosBtn")}
                        </button>
                        {photosCarId === c.dossierId && (
                          <FotosDoCarro companyId={c.companyId} dossierId={c.dossierId} />
                        )}
                      </div>
                    )}
                    {claimDevisQuery.isLoading && <p className="text-xs text-neutral-400">…</p>}
                    {claimDevisQuery.error && (
                      <p className="text-xs text-red-400">{claimDevisQuery.error.message}</p>
                    )}
                    {claimDevisQuery.data && (
                      <div className="space-y-1.5">
                        <p className="text-xs uppercase tracking-wider text-neutral-400">
                          {claimDevisQuery.data.externalDevisRef ??
                            claimDevisQuery.data.dossierCode}
                          {claimDevisQuery.data.devisDate && ` · ${claimDevisQuery.data.devisDate}`}
                          {claimDevisQuery.data.impactsTotal != null &&
                            ` · ${claimDevisQuery.data.impactsTotal} ${t("techPortal.impactsWord")}`}
                        </p>
                        {claimDevisQuery.data.panels.length === 0 && (
                          <p className="text-xs text-neutral-400">
                            {t("techPortal.claimDevisNoPanels")}
                          </p>
                        )}
                        {claimDevisQuery.data.panels.map((pl, i) => (
                          <div key={i} className="flex items-start justify-between gap-2 text-sm">
                            <span className="min-w-0">
                              {trPanel(pl.panel, locale)}
                              {pl.note && (
                                <span className="block text-xs text-amber-300">
                                  ⚠ {trNote(pl.note, locale)}
                                </span>
                              )}
                            </span>
                            <span className="flex shrink-0 items-center gap-1.5">
                              {pl.impacts != null && (
                                <span className="font-mono text-xs text-neutral-400">
                                  {pl.impacts}×
                                </span>
                              )}
                              {pl.sizeClass && (
                                <span className="rounded bg-neutral-800 px-1.5 py-0.5 text-xs text-neutral-300">
                                  {pl.sizeClass}
                                </span>
                              )}
                              <span
                                className={`rounded px-1.5 py-0.5 text-xs font-semibold ${
                                  pl.method.toUpperCase() === "DSP"
                                    ? "bg-lime/15 text-lime"
                                    : pl.method.toUpperCase() === "DAP"
                                      ? "bg-amber-500/15 text-amber-300"
                                      : /ECHANGE|REMPLACEMENT/i.test(pl.method)
                                        ? "bg-red-500/15 text-red-300"
                                        : "bg-blue-500/15 text-blue-300"
                                }`}
                              >
                                {trMethod(pl.method, locale)}
                              </span>
                            </span>
                          </div>
                        ))}
                        {claimDevisQuery.data.deposeRepose && (
                          <p className="border-t border-neutral-800 pt-1.5 text-xs text-neutral-400">
                            {t("techPortal.deposeReposeLabel")}:{" "}
                            {trDepose(claimDevisQuery.data.deposeRepose, locale)}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* F-chantier-agenda — agenda do chantier (somente leitura) */}
        {agChantiers.length > 0 && (
          <section className="pt-2">
            <div className="mb-2 flex items-center justify-between gap-2">
              <h2 className="text-xs uppercase tracking-wider text-neutral-400">
                {t("techPortal.agendaTitle")}
              </h2>
              <select
                value={effectiveAgChantierId ?? ""}
                onChange={(e) => setAgChantierId(Number(e.target.value))}
                className="rounded-md border border-neutral-700 bg-neutral-900 px-2 py-1.5 text-xs"
              >
                {agChantiers.map((ch) => (
                  <option key={ch.id} value={ch.id}>
                    🏗 {ch.name}
                  </option>
                ))}
              </select>
            </div>
            {(techAgendaQuery.data?.rows ?? []).length === 0 && (
              <p className="text-sm text-neutral-400">{t("techPortal.agendaEmpty")}</p>
            )}
            <div className="space-y-1.5">
              {(techAgendaQuery.data?.rows ?? []).map((a) => (
                <div
                  key={a.id}
                  className="flex items-center justify-between gap-2 rounded-lg border border-neutral-800 bg-neutral-900/60 px-3 py-2 text-sm"
                >
                  <span className="min-w-0 truncate">
                    <span className="font-mono">{a.date}</span>
                    <span className="font-semibold text-lime"> {a.time}</span>
                    {a.plate && <span className="font-mono"> · {a.plate}</span>}
                    {a.vehicleLabel && (
                      <span className="text-neutral-400"> · {a.vehicleLabel}</span>
                    )}
                    {a.customerName && (
                      <span className="text-neutral-400"> · {a.customerName}</span>
                    )}
                  </span>
                  <span
                    className={`shrink-0 rounded px-1.5 py-0.5 text-xs ${
                      a.status === "scheduled"
                        ? "bg-sky-500/15 text-sky-300"
                        : a.status === "arrived"
                          ? "bg-amber-500/15 text-amber-300"
                          : "bg-lime/15 text-lime"
                    }`}
                  >
                    {a.status}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      {/* F-portal-busca-primeiro — barra inferior fixa com o scanner no
          centro (no pátio é o gesto nº 1). */}
      <nav
        className="fixed inset-x-0 bottom-0 z-30 border-t border-neutral-800 bg-neutral-900/95 backdrop-blur"
        data-testid="portal-bottom-bar"
      >
        <div className="mx-auto grid max-w-xl grid-cols-5 items-end px-2 pb-[max(env(safe-area-inset-bottom),6px)] pt-1.5 text-xs">
          <button
            type="button"
            onClick={() => irPara("mes-carros")}
            data-testid="nav-cars"
            className="flex flex-col items-center gap-0.5 py-1 text-neutral-200"
          >
            <span aria-hidden="true" className="text-lg leading-none">
              🚗
            </span>
            <span>{t("techPortal.navCars")}</span>
          </button>
          <button
            type="button"
            onClick={() => irPara("worklog")}
            data-testid="nav-worklog"
            className="flex flex-col items-center gap-0.5 py-1 text-neutral-200"
          >
            <span aria-hidden="true" className="text-lg leading-none">
              📋
            </span>
            <span>{t("techPortal.navWorklog")}</span>
          </button>
          {somenteConsulta ? (
            <span />
          ) : (
            <button
              type="button"
              onClick={() => {
                window.scrollTo({ top: 0 });
                openCamera(mode);
              }}
              disabled={scanning != null}
              data-testid="nav-scan"
              className="-mt-6 flex flex-col items-center gap-0.5 disabled:opacity-50"
            >
              <span
                aria-hidden="true"
                className="flex h-14 w-14 items-center justify-center rounded-full bg-lime text-2xl text-on-lime shadow-lg ring-4 ring-neutral-900"
              >
                📷
              </span>
              <span className="font-semibold text-lime">{t("techPortal.navScanner")}</span>
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setClaimsStatus("pending");
              irPara("mes-carros");
            }}
            data-testid="nav-payments"
            className="flex flex-col items-center gap-0.5 py-1 text-neutral-200"
          >
            <span aria-hidden="true" className="text-lg leading-none">
              💶
            </span>
            <span>{t("techPortal.navPayments")}</span>
          </button>
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            data-testid="nav-more"
            className="flex flex-col items-center gap-0.5 py-1 text-neutral-200"
          >
            <span aria-hidden="true" className="text-lg leading-none">
              ⋯
            </span>
            <span>{t("techPortal.navMore")}</span>
          </button>
        </div>
      </nav>
    </div>
  );
}

/**
 * F2 checklist — bloco compartilhado com upload de foto via tRPC do PORTAL
 * DO TÉCNICO (compressão no aparelho → base64 → volume do Railway).
 */
function TechChecklistBlock({
  companyId,
  dossierId,
  value,
  onChange,
  alreadySigned,
  montador = false,
  showSignature = false,
}: {
  companyId: number;
  dossierId: number;
  value: ChecklistDraft;
  onChange: (v: ChecklistDraft) => void;
  alreadySigned: boolean;
  /** F-montador-recepcao-curta — 4 fotos em vez de 8. */
  montador?: boolean;
  /**
   * CEO 2026-09-23 — assinatura só quando o SÊNIOR recebe carro de CHANTIER
   * (o servidor decide: `signatureRequired`). Montador e atelier: nunca.
   */
  showSignature?: boolean;
}) {
  const t = useT();
  const utils = trpc.useUtils();
  const photosQuery = trpc.technicianPortal.listChecklistPhotos.useQuery({
    companyId,
    dossierId,
    checkKind: "reception",
  });
  const uploadMut = trpc.technicianPortal.uploadChecklistPhoto.useMutation({
    onSuccess: () => utils.technicianPortal.listChecklistPhotos.invalidate(),
  });
  const [uploadingKind, setUploadingKind] = useState<string | null>(null);
  // F-foto-erro-visivel (CEO 2026-09-21: "lançou as fotos e não aparece") —
  // o catch era VAZIO: foto recusada ou que nem saiu do aparelho sumia sem
  // aviso e o técnico jurava que tinha enviado. Agora o motivo aparece.
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  async function handleUpload(kind: PhotoSlotKind, file: File) {
    setUploadingKind(kind);
    setUploadErr(null);
    try {
      const b64 = await compressImageToBase64(file);
      await uploadMut.mutateAsync({
        companyId,
        dossierId,
        checkKind: "reception",
        kind,
        contentBase64: b64,
      });
    } catch (e) {
      setUploadErr(e instanceof Error ? e.message : String(e));
    } finally {
      setUploadingKind(null);
    }
  }
  return (
    <>
      {uploadErr && (
        <p className="rounded-md bg-red-100 px-3 py-2 text-xs font-semibold text-red-700">
          ✕ {t("techPortal.photoUploadFailPre")} {uploadErr}
        </p>
      )}
      <ChecklistExtras
        value={value}
        onChange={onChange}
        photos={photosQuery.data?.rows ?? []}
        onUploadPhoto={handleUpload}
        uploadingKind={uploadingKind}
        alreadySigned={alreadySigned}
        showMileage={false}
        montador={montador}
        tecnico
        showSignature={showSignature}
        tone="light"
      />
    </>
  );
}

/**
 * F2 — recepção JÁ salva (franquia respondida) mas checklist de 8 pontos
 * incompleto: card âmbar para completar km/pára-brisa/ciel/fotos/assinatura
 * sem mexer na franquia (reenviada como está). Completo → selo verde.
 */
function TechChecklistCompleteCard({
  companyId,
  montador = false,
  signatureRequired = false,
  d,
}: {
  companyId: number;
  /** F-montador-recepcao-curta — o montador fecha a recepção com 4 fotos. */
  montador?: boolean;
  /** CEO 2026-09-23 — sênior em chantier: a assinatura conta como falta. */
  signatureRequired?: boolean;
  d: {
    id: number;
    receptionFranchise: boolean;
    receptionFranchiseAmountTtc: number | null;
    receptionMileageKm: number | null;
    receptionPareBriseOk: boolean | null;
    receptionCielOk: boolean | null;
    receptionSigned: boolean;
  };
}) {
  const t = useT();
  const utils = trpc.useUtils();
  const [chk, setChk] = useState<ChecklistDraft>({
    mileage: d.receptionMileageKm != null ? String(d.receptionMileageKm) : "",
    pareBrise: d.receptionPareBriseOk == null ? "" : d.receptionPareBriseOk ? "ok" : "nok",
    ciel: d.receptionCielOk == null ? "" : d.receptionCielOk ? "ok" : "nok",
    signature: null,
    signedName: "",
  });
  const photosQuery = trpc.technicianPortal.listChecklistPhotos.useQuery({
    companyId,
    dossierId: d.id,
    checkKind: "reception",
  });
  // F-chk-falta (CEO 2026-09-21) — este card não dizia nada ao salvar: se
  // ainda faltava foto, ele voltava idêntico e o técnico lia isso como
  // travamento (GP-108-AE: seis salvamentos seguidos e um "o app travou").
  const [aviso, setAviso] = useState<{ tone: "ok" | "error"; text: string } | null>(null);
  const saveMut = trpc.technicianPortal.saveReceptionCheck.useMutation({
    onSuccess: () => {
      setAviso({ tone: "ok", text: t("techPortal.chkSavedMsg") });
      void utils.technicianPortal.invalidate();
    },
    onError: (e) => setAviso({ tone: "error", text: e.message }),
  });
  const kinds = new Set((photosQuery.data?.rows ?? []).map((p) => p.kind));
  const falta = checklistMissing({
    pareBriseOk: d.receptionPareBriseOk,
    cielOk: d.receptionCielOk,
    signed: d.receptionSigned,
    photoKinds: kinds,
    montador,
    tecnico: true,
    requireSignature: signatureRequired,
  });
  const complete = checklistComplete({
    mileageKm: d.receptionMileageKm,
    pareBriseOk: d.receptionPareBriseOk,
    cielOk: d.receptionCielOk,
    signed: d.receptionSigned,
    photoKinds: kinds,
    montador,
    tecnico: true,
    requireSignature: signatureRequired,
  });
  if (photosQuery.isLoading) return null;
  if (complete) {
    return (
      <p className="rounded-md border border-lime/30 bg-lime/5 px-3 py-2 text-xs font-semibold text-lime">
        ✓ {t("techPortal.chkCompleteBadge")}
      </p>
    );
  }
  return (
    <div
      className="space-y-2 rounded-lg border p-3"
      style={{ background: "#FEF9C3", borderColor: "#CA8A04", color: "#854D0E" }}
    >
      <p className="text-sm font-bold">📋 {t("techPortal.chkTitle")}</p>
      <p className="text-xs">{t("techPortal.chkIncompleteHint")}</p>
      {/* O que exatamente falta — em vez de um card âmbar mudo. */}
      <p className="rounded-md bg-white/70 px-2.5 py-1.5 text-xs font-semibold">
        ⚠ {t("techPortal.chkMissingPre")}{" "}
        {[
          falta.photos.length > 0
            ? `${falta.photos.length} ${
                falta.photos.length === 1
                  ? t("techPortal.chkMissingPhoto")
                  : t("techPortal.chkMissingPhotos")
              } (${t(photoSlotLabelKey(falta.photos[0]!))})`
            : null,
          falta.pareBrise ? t("techPortal.chkMissingPareBrise") : null,
          falta.ciel ? t("techPortal.chkMissingCiel") : null,
          falta.signature ? t("techPortal.chkMissingSignature") : null,
        ]
          .filter(Boolean)
          .join(" · ")}
      </p>
      {aviso && (
        <p
          className={`rounded-md px-2.5 py-1.5 text-xs font-semibold ${
            aviso.tone === "ok" ? "bg-white/70" : "bg-red-100 text-red-700"
          }`}
        >
          {aviso.tone === "ok" ? "✔ " : "✕ "}
          {aviso.text}
        </p>
      )}
      <TechChecklistBlock
        companyId={companyId}
        dossierId={d.id}
        value={chk}
        onChange={setChk}
        alreadySigned={d.receptionSigned}
        montador={montador}
        showSignature={signatureRequired}
      />
      <button
        onClick={() =>
          saveMut.mutate({
            companyId,
            dossierId: d.id,
            franchise: d.receptionFranchise,
            franchiseAmountTtc: d.receptionFranchiseAmountTtc,
            mileageKm: chk.mileage.trim() !== "" ? Math.round(Number(chk.mileage)) : null,
            pareBriseOk: chk.pareBrise === "" ? null : chk.pareBrise === "ok",
            cielOk: chk.ciel === "" ? null : chk.ciel === "ok",
            signatureBase64: chk.signature,
            signedByName: chk.signedName.trim() || null,
          })
        }
        disabled={saveMut.isPending}
        className="w-full rounded-lg px-4 py-3 text-sm font-bold disabled:opacity-40"
        style={{ background: "#CA8A04", color: "#fff" }}
      >
        {saveMut.isPending ? t("techPortal.chkSaving") : `✔ ${t("techPortal.chkSaveBtn")}`}
      </button>
    </div>
  );
}

/**
 * F-franchise-late — editor tardio da franquia (sim/não + valor TTC), usado
 * quando a recepção já foi feita: o cliente pode ter esquecido de informar o
 * desconto no início do serviço. Salvar reaplica o cálculo central.
 */
function LateFranchiseEditor({
  companyId: _companyId,
  dossierId: _dossierId,
  initialYes,
  initialAmount,
  saving,
  onSave,
}: {
  companyId: number;
  dossierId: number;
  initialYes: boolean;
  initialAmount: number | null;
  saving: boolean;
  onSave: (franchise: boolean, amountTtc: number | null) => void;
}) {
  const t = useT();
  const [yes, setYes] = useState<boolean>(initialYes);
  const [amount, setAmount] = useState<string>(initialAmount != null ? String(initialAmount) : "");
  return (
    <div
      className="space-y-2 rounded-md border p-2.5"
      style={{ background: "#FEF9C3", borderColor: "#CA8A04", color: "#854D0E" }}
    >
      <p className="text-xs font-semibold">{t("techPortal.recFranchiseQ")}</p>
      <div className="flex gap-2">
        {([true, false] as const).map((v) => (
          <button
            key={String(v)}
            onClick={() => setYes(v)}
            className="rounded-md px-3 py-1.5 text-xs font-bold"
            style={
              yes === v
                ? { background: "#CA8A04", color: "#fff" }
                : { background: "#fff", color: "#854D0E", border: "1px solid #CA8A04" }
            }
          >
            {v ? t("techPortal.recYes") : t("techPortal.recNo")}
          </button>
        ))}
      </div>
      {yes && (
        <input
          type="number"
          inputMode="decimal"
          min={0}
          step="0.01"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder={t("techPortal.recAmountPh")}
          className="w-full rounded-md border px-3 py-2 text-sm"
          style={{ borderColor: "#CA8A04", background: "#fff", color: "#1F2937" }}
        />
      )}
      <button
        onClick={() => onSave(yes, yes ? Number(amount) : null)}
        disabled={saving || (yes && (!amount || Number(amount) <= 0))}
        className="w-full rounded-md px-3 py-2 text-xs font-bold disabled:opacity-40"
        style={{ background: "#CA8A04", color: "#fff" }}
      >
        {saving ? t("techPortal.recSaving") : `✔ ${t("techPortal.recSaveBtn")}`}
      </button>
    </div>
  );
}

/** F-worklog — worklog da semana do PRÓPRIO técnico, por cliente (PDF). */
function TechWorklogBlock({ companyId }: { companyId: number }) {
  const t = useT();
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  });
  function shiftWeek(dir: -1 | 1) {
    setWeekStart((w) => {
      const d = new Date(w + "T00:00:00");
      d.setDate(d.getDate() + dir * 7);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    });
  }
  function weekNum(w: string): number {
    const d = new Date(w + "T00:00:00Z");
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  }
  const weekQ = trpc.worklogs.techWeek.useQuery({ companyId, weekStart });
  const abaPdf = useAbaPendente();
  const pdfMut = trpc.worklogs.techPdf.useMutation({
    onSuccess: (r) => abaPdf.ir(r.url),
    onError: () => abaPdf.fechar(),
  });
  const clients = weekQ.data?.clients ?? [];
  return (
    <div className="mb-2 rounded-lg border border-neutral-800 bg-neutral-900/60 px-3 py-2 text-sm">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs uppercase tracking-wider text-neutral-400">
          🗒 {t("techPortal.worklogTitle")}
        </span>
        <span className="flex items-center overflow-hidden rounded-md border border-neutral-700 text-xs">
          <button
            type="button"
            className="px-2 py-0.5 hover:bg-neutral-800"
            onClick={() => shiftWeek(-1)}
          >
            −
          </button>
          <span className="border-x border-neutral-700 px-2 py-0.5 font-semibold">
            {t("techPortal.worklogWeekPre")} {weekNum(weekStart)}
          </span>
          <button
            type="button"
            className="px-2 py-0.5 hover:bg-neutral-800"
            onClick={() => shiftWeek(1)}
          >
            +
          </button>
        </span>
      </div>
      {clients.length === 0 && (
        <p className="pt-1 text-xs text-neutral-400">{t("techPortal.worklogEmpty")}</p>
      )}
      {clients.map((c) => (
        <div key={c.customerId} className="flex items-center justify-between gap-2 pt-1.5">
          <span className="min-w-0 truncate text-xs text-neutral-300">
            {c.customerName}
            {/* CEO 2026-09-22: carros, não linhas de serviço. */}
            <span className="text-neutral-400">
              {" "}
              · {c.count} {c.count === 1 ? t("worklog.carWordSingular") : t("worklog.carsWord")}
            </span>
          </span>
          <button
            type="button"
            disabled={pdfMut.isPending}
            onClick={() => {
              abaPdf.abrir();
              pdfMut.mutate({ companyId, customerId: c.customerId, weekStart });
            }}
            className="shrink-0 rounded-md border border-lime/50 px-2 py-0.5 text-xs text-lime hover:bg-lime/10 disabled:opacity-40"
          >
            📄 {t("techPortal.worklogPdfBtn")}
          </button>
        </div>
      ))}
    </div>
  );
}

/**
 * F-montador-fases (CEO 2026-09-20) — o trabalho do montador são DOIS:
 * desmontar e montar; cada metade vale metade da comissão do par.
 * CEO 2026-09-29: SEM fotos nas etapas — o técnico fotografa só na recepção
 * (quando é ele quem recebe); o gerente fotografa na recepção e na entrega.
 */
function MontadorPhasesBlock({
  companyId,
  dossierId,
  montadorName,
  demontageMine,
  montageMine,
  demontageDone,
  montageDone,
  montageTaken,
  montageExternal,
  perPhase,
  onConcluded,
}: {
  companyId: number;
  dossierId: number;
  /** F-montagem-externa — montagem feita por terceiro (garage): ninguém recebe. */
  montageExternal: boolean;
  /** F-etapa-fechada — a página fecha o card e mostra o aviso. */
  onConcluded?: (phase: "demontage" | "montage", amount: number | null) => void;
  /** Quem assumiu a montagem — para QUALQUER técnico ver o estado do carro. */
  montadorName: string | null;
  /**
   * F-metade-dono (CEO 2026-09-23) — cada metade tem o seu dono. Quem pegou
   * SÓ a montagem de uma linha alheia (Volodymyr no FG-284-SR) precisa do
   * bloco de fotos da montagem — antes o bloco inteiro dependia de a LINHA
   * ser minha, e ele não tinha onde fotografar.
   */
  demontageMine: boolean;
  montageMine: boolean;
  demontageDone: boolean;
  montageDone: boolean;
  /**
   * F-montagem-duas-etapas (CEO 2026-09-23) — o dono já disse "pronto,
   * montagem"? Antes disso a 2ª etapa não aparece para ele e a montagem
   * segue em aberto para outro técnico.
   */
  montageTaken: boolean;
  perPhase: number | null;
}) {
  const t = useT();
  const utils = trpc.useUtils();
  const [err, setErr] = useState<string | null>(null);
  const takeMut = trpc.technicianPortal.takeMontagePhase.useMutation({
    onSuccess: async () => {
      setErr(null);
      await utils.technicianPortal.findByPlate.invalidate();
      await utils.technicianPortal.myCarsAll.invalidate();
    },
    onError: (e) => setErr(e.message),
  });
  // F-montagem-externa (CEO 2026-10-10) — "a montagem foi feita por terceiro".
  const externalMut = trpc.technicianPortal.setMontageExternal.useMutation({
    onSuccess: async () => {
      setErr(null);
      await utils.technicianPortal.findByPlate.invalidate();
      await utils.technicianPortal.myCarsAll.invalidate();
      await utils.technicianPortal.me.invalidate();
    },
    onError: (e) => setErr(e.message),
  });
  const declararTerceiro = () => {
    if (!window.confirm(t("techPortal.montageExternalConfirm"))) return;
    externalMut.mutate({ companyId, dossierId });
  };
  // F-montagem-duas-etapas — "pronto, montagem" do próprio dono.
  const takeOwnMut = trpc.technicianPortal.takeOwnMontage.useMutation({
    onSuccess: async () => {
      setErr(null);
      await utils.technicianPortal.findByPlate.invalidate();
      await utils.technicianPortal.myCarsAll.invalidate();
    },
    onError: (e) => setErr(e.message),
  });
  /**
   * F-fase-confirmacao (CEO 2026-09-21) — "quando o desmontador realiza a
   * desmontagem uma mensagem deve informar que o carro foi desmontado e o
   * valor da desmontagem". Antes o botão sumia e aparecia um ✓ discreto: não
   * dava para saber se tinha registrado nem quanto valeu.
   */
  const [confirmado, setConfirmado] = useState<"demontage" | "montage" | null>(null);
  const phaseMut = trpc.technicianPortal.setMontagePhase.useMutation({
    onSuccess: async (_d, vars) => {
      setErr(null);
      setConfirmado(vars.done ? vars.phase : null);
      // Valor REAL vindo do servidor (a estimativa do card pode diferir da
      // base da linha — o aviso tem de bater com a lista).
      if (vars.done) onConcluded?.(vars.phase, _d.amount ?? perPhase);
      await utils.technicianPortal.findByPlate.invalidate();
      await utils.technicianPortal.myCarsAll.invalidate();
    },
    onError: (e) => setErr(e.message),
  });
  const money = (v: number) =>
    v.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

  function PhaseButton({ phase, done }: { phase: "demontage" | "montage"; done: boolean }) {
    if (!(phase === "demontage" ? demontageMine : montageMine)) return null;
    if (done) {
      return (
        <span className="text-xs font-semibold text-lime">
          ✓{" "}
          {phase === "demontage"
            ? t("techPortal.phaseDemontageDone")
            : t("techPortal.phaseMontageDone")}
          {perPhase != null ? ` · ${money(perPhase)}` : ""}
        </span>
      );
    }
    return (
      <button
        type="button"
        disabled={phaseMut.isPending}
        onClick={() => phaseMut.mutate({ companyId, dossierId, phase, done: true })}
        // F-fase-botao (CEO 2026-09-20): era contornado e com ✔, então lia-se
        // como "já está feito" — o CEO completou as 22 fotos e não clicou em
        // nada, achando que o botão era um selo. Agora é um botão de ação.
        className="rounded-md bg-lime px-2.5 py-1 text-xs font-semibold text-on-lime disabled:opacity-40"
      >
        {phase === "demontage"
          ? t("techPortal.phaseDemontageBtn")
          : t("techPortal.phaseMontageBtn")}
        {perPhase != null ? ` (+${money(perPhase)})` : ""}
      </button>
    );
  }

  return (
    <div className="space-y-2">
      {/* F-montador-estado (CEO 2026-09-20) — o estado das fases tem de ser
          visível para QUALQUER técnico que digite a placa, não só para o dono:
          "desmontado por X, falta a montagem" é o que permite a outro decidir
          se pega o carro. Antes, quem não fosse o dono não via nada. */}
      {montadorName != null && !montageMine && !demontageMine && demontageDone && (
        <div
          className={`rounded-md px-2 py-1.5 text-xs ${
            montageDone
              ? "bg-lime/10 text-lime"
              : "border border-amber-500/40 bg-amber-500/10 text-amber-200"
          }`}
        >
          <p>
            {montageExternal
              ? `✓ ${t("techPortal.phaseDemontageByPre")} ${montadorName} · ${t("techPortal.montageExternalDone")}`
              : montageDone
                ? `✓ ${t("techPortal.phaseBothDoneBy")} ${montadorName}`
                : `🔧 ${t("techPortal.phaseDemontageByPre")} ${montadorName} — ${t("techPortal.phaseMontagePending")}`}
          </p>
          {/* F-montagem-compartilhada — o carro parado pela metade pode ser
              terminado por outro: ele leva a metade dele, pelo percentual do
              par DELE com o cliente. */}
          {!montageDone && !montageTaken && (
            <div className="mt-1.5">
              <button
                type="button"
                disabled={takeMut.isPending}
                onClick={() => takeMut.mutate({ companyId, dossierId, phase: "montage" })}
                className="rounded-md bg-amber-500 px-2.5 py-1 text-xs font-semibold text-neutral-900 disabled:opacity-50"
              >
                {t("techPortal.takeMontageBtn")}
              </button>
              <p className="mt-1 text-xs text-neutral-400">{t("techPortal.takeHint")}</p>
              {/* CEO 2026-10-10 (FE-710-EC): o garage remontou — ninguém recebe. */}
              <button
                type="button"
                disabled={externalMut.isPending}
                onClick={declararTerceiro}
                className="mt-2 rounded-md border border-amber-400/60 px-2.5 py-1 text-xs font-semibold text-amber-100 hover:border-amber-300 disabled:opacity-50"
                data-testid="montage-external-btn"
              >
                {t("techPortal.montageExternalBtn")}
              </button>
              <p className="mt-1 text-xs text-neutral-400">{t("techPortal.montageExternalHint")}</p>
            </div>
          )}
        </div>
      )}
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-semibold text-neutral-300">
            1 · {t("techPortal.phaseDemontageTitle")}
          </span>
          <PhaseButton phase="demontage" done={demontageDone} />
        </div>
      </div>
      {/* F-montagem-duas-etapas (CEO 2026-09-23): "primeira etapa,
          desmontagem; depois perguntar: pronto, montagem". A 2ª etapa só
          existe depois da desmontagem concluída — e começa com a pergunta,
          não com as fotos. */}
      {montageMine && !demontageDone && (
        <p className="text-xs text-neutral-400">{t("techPortal.phaseMontageAfterDemontage")}</p>
      )}
      {montageMine && demontageDone && !montageTaken && (
        <div className="space-y-1 rounded-md border border-lime/40 bg-lime/5 px-2.5 py-2">
          <p className="text-xs font-semibold text-neutral-200">
            2 · {t("techPortal.phaseMontageTitle")}
          </p>
          <p className="text-xs text-neutral-400">{t("techPortal.takeOwnMontageHint")}</p>
          <button
            type="button"
            disabled={takeOwnMut.isPending}
            onClick={() => takeOwnMut.mutate({ companyId, dossierId })}
            className="rounded-md bg-lime px-3 py-1.5 text-xs font-semibold text-on-lime disabled:opacity-40"
          >
            {t("techPortal.takeOwnMontageBtn")}
            {perPhase != null ? ` (+${money(perPhase)})` : ""}
          </button>
          <button
            type="button"
            disabled={externalMut.isPending}
            onClick={declararTerceiro}
            className="ml-2 rounded-md border border-neutral-600 px-2.5 py-1.5 text-xs font-semibold text-neutral-200 hover:border-amber-300 disabled:opacity-40"
          >
            {t("techPortal.montageExternalBtn")}
          </button>
        </div>
      )}
      {montageMine && demontageDone && montageTaken && (
        <div className="space-y-1">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold text-neutral-300">
              2 · {t("techPortal.phaseMontageTitle")}
            </span>
            <PhaseButton phase="montage" done={montageDone} />
          </div>
        </div>
      )}
      {confirmado && (
        <p className="rounded-md border border-lime/40 bg-lime/10 px-2.5 py-2 text-xs font-semibold text-lime">
          ✓{" "}
          {confirmado === "demontage"
            ? t("techPortal.phaseDoneDemontageMsg")
            : t("techPortal.phaseDoneMontageMsg")}
          {perPhase != null ? ` · ${money(perPhase)}` : ""}
          <span className="block font-normal text-neutral-300">
            {confirmado === "demontage"
              ? t("techPortal.phaseDoneDemontageHint")
              : t("techPortal.phaseDoneMontageHint")}
          </span>
        </p>
      )}
      {err && <p className="text-xs text-red-400">{err}</p>}
    </div>
  );
}

/**
 * F-mycars-fotos (CEO 2026-09-20) — as fotos do carro DENTRO do card de
 * "Meus carros". Só leitura: aqui é para conferir o que foi registrado, não
 * para fotografar — quem fotografa é o dono da etapa, no card do carro.
 */
function FotosDoCarro({ companyId, dossierId }: { companyId: number; dossierId: number }) {
  const t = useT();
  const etapas = [
    { kind: "reception" as const, rotulo: t("techPortal.photosReception") },
    { kind: "montage" as const, rotulo: t("techPortal.photosDemontage") },
    { kind: "montage_end" as const, rotulo: t("techPortal.photosMontageEnd") },
  ];
  return (
    <div className="mt-1.5 space-y-2">
      {etapas.map((e) => (
        <EtapaDeFotos key={e.kind} companyId={companyId} dossierId={dossierId} {...e} />
      ))}
    </div>
  );
}

function EtapaDeFotos({
  companyId,
  dossierId,
  kind,
  rotulo,
}: {
  companyId: number;
  dossierId: number;
  kind: "reception" | "montage" | "montage_end";
  rotulo: string;
}) {
  const q = trpc.technicianPortal.listChecklistPhotos.useQuery({
    companyId,
    dossierId,
    checkKind: kind,
  });
  const fotos = q.data?.rows ?? [];
  if (fotos.length === 0) return null;
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-neutral-400">
        {rotulo} · {fotos.length}
      </p>
      <div className="mt-1 flex flex-wrap gap-1">
        {fotos.map((f) => (
          <a key={f.url} href={f.url} target="_blank" rel="noreferrer">
            <img
              src={f.url}
              alt={f.kind}
              className="h-12 w-12 rounded border border-neutral-700 object-cover"
            />
          </a>
        ))}
      </div>
    </div>
  );
}

/**
 * F-carro-parc (CEO 2026-09-26) — carro de PARC: sem devis, o técnico escolhe
 * a categoria pré-cadastrada no Parc Grêle do cliente (vê só o valor DELE),
 * informa marca e modelo, e o carro vira dossier normal — em seguida faz a
 * recepção (5 fotos) e assume PDR/montagem como qualquer carro.
 */
function ParcCarEntry({
  companyId,
  plate,
  mode,
  onOpenImport,
  onCreated,
  liberadoParaNovoCarro,
}: {
  companyId: number;
  plate: string;
  /** F-parc-vin — busca feita por placa ou por VIN (furgão sem placa). */
  mode: "plate" | "vin";
  onOpenImport: () => void;
  onCreated: (plate: string) => void;
  /** F-tarefas-popup — com PDR pendente, reabre o pop-up em vez de lançar. */
  liberadoParaNovoCarro: () => boolean;
}) {
  const t = useT();
  const optsQ = trpc.technicianPortal.parcOptions.useQuery({ companyId });
  const [aberto, setAberto] = useState(false);
  const [parcId, setParcId] = useState<number | null>(null);
  const [forfaitId, setForfaitId] = useState<number | null>(null);
  const [atelierId, setAtelierId] = useState<number | null>(null);
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const createMut = trpc.technicianPortal.createParcCar.useMutation({
    onSuccess: (r) => {
      setAberto(false);
      onCreated(r.plate);
    },
    onError: (e) => setErro(e.message),
  });
  const parcs = optsQ.data?.parcs ?? [];
  const parc =
    parcs.find((p) => p.parcId === parcId) ?? (parcs.length === 1 ? parcs[0] : undefined);
  const precisaAtelier = (parc?.ateliers.length ?? 0) > 0;
  const pronto =
    parc != null &&
    forfaitId != null &&
    make.trim() !== "" &&
    model.trim() !== "" &&
    (!precisaAtelier || atelierId != null);
  const campo =
    "w-full rounded-md border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm text-neutral-100";
  return (
    <div className="mt-2 space-y-2 border-t border-neutral-800 pt-2">
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={onOpenImport}
          className="rounded-md border border-lime/60 px-3 py-2 text-xs font-semibold text-lime"
        >
          📄 {t("techPortal.parcImportPdfBtn")}
        </button>
        <button
          type="button"
          data-testid="parc-car-btn"
          disabled={parcs.length === 0}
          title={parcs.length === 0 ? t("techPortal.parcNoneHint") : undefined}
          onClick={() => {
            if (!aberto && !liberadoParaNovoCarro()) return;
            setErro(null);
            setAberto(!aberto);
          }}
          className="rounded-md border border-sky-500/60 px-3 py-2 text-xs font-semibold text-sky-300 disabled:opacity-40"
        >
          🅿 {t("techPortal.parcCarBtn")}
        </button>
      </div>
      {parcs.length === 0 && !optsQ.isLoading && (
        <p className="text-xs text-neutral-400">{t("techPortal.parcNoneHint")}</p>
      )}
      {aberto && parcs.length > 0 && (
        <div
          className="space-y-2 rounded-lg border border-sky-500/40 bg-sky-500/5 p-3"
          data-testid="parc-car-form"
        >
          <p className="text-xs text-neutral-300">
            {mode === "vin" ? "VIN:" : t("techPortal.parcPlateLabel")}{" "}
            <strong className="font-mono">{plate}</strong>
          </p>
          {parcs.length > 1 && (
            <select
              value={parc?.parcId ?? ""}
              onChange={(e) => {
                setParcId(e.target.value ? Number(e.target.value) : null);
                setForfaitId(null);
                setAtelierId(null);
              }}
              className={campo}
              data-testid="parc-select"
            >
              <option value="">{t("techPortal.parcChooseClient")}</option>
              {parcs.map((p) => (
                <option key={p.parcId} value={p.parcId}>
                  {p.customerName} · {p.parcName}
                </option>
              ))}
            </select>
          )}
          {parc && (
            <>
              <p className="text-xs text-neutral-400">
                {parc.customerName} · {parc.parcName}
              </p>
              <select
                value={forfaitId ?? ""}
                onChange={(e) => setForfaitId(e.target.value ? Number(e.target.value) : null)}
                className={campo}
                data-testid="parc-forfait"
              >
                <option value="">{t("techPortal.parcChooseCategory")}</option>
                {parc.forfaits.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.myValueWithMontage != null
                      ? `${f.label} — ${t("techPortal.parcOnlyPdr")} ${formatMoney(f.myValue, { decimals: 2 })} · ${t("techPortal.parcWithMontage")} ${formatMoney(f.myValueWithMontage, { decimals: 2 })}`
                      : `${f.label} — ${t("techPortal.parcYouReceive")} ${formatMoney(f.myValue, { decimals: 2 })}`}
                  </option>
                ))}
              </select>
              {parc.forfaits.some((f) => f.myValueWithMontage != null) && (
                <p className="text-xs text-neutral-400" data-testid="parc-choice-later">
                  {t("techPortal.parcChoiceLater")}
                </p>
              )}
              {precisaAtelier && (
                <select
                  value={atelierId ?? ""}
                  onChange={(e) => setAtelierId(e.target.value ? Number(e.target.value) : null)}
                  className={campo}
                  data-testid="parc-atelier"
                >
                  <option value="">{t("techPortal.parcChooseAtelier")}</option>
                  {parc.ateliers.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                      {a.city ? ` — ${a.city}` : ""}
                    </option>
                  ))}
                </select>
              )}
              <div className="grid grid-cols-2 gap-2">
                <input
                  value={make}
                  onChange={(e) => setMake(e.target.value)}
                  placeholder={t("techPortal.parcMakePh")}
                  className={campo}
                  data-testid="parc-make"
                />
                <input
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  placeholder={t("techPortal.parcModelPh")}
                  className={campo}
                  data-testid="parc-model"
                />
              </div>
              {erro && <p className="text-xs text-red-400">{erro}</p>}
              <button
                type="button"
                disabled={!pronto || createMut.isPending}
                data-testid="parc-create"
                onClick={() =>
                  createMut.mutate({
                    companyId,
                    forfaitId: forfaitId!,
                    ...(mode === "vin"
                      ? { vin: plate.toUpperCase().replace(/[^A-Z0-9]/gi, "") }
                      : { plate }),
                    make: make.trim(),
                    model: model.trim(),
                    atelierId: atelierId ?? undefined,
                  })
                }
                className="w-full rounded-lg bg-lime px-4 py-2.5 text-sm font-semibold text-on-lime disabled:opacity-40"
              >
                {createMut.isPending ? "…" : `✔ ${t("techPortal.parcCreateBtn")}`}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * F-adiantamento-tecnico (CEO 2026-10-25) — "Avances à déduire": o que a
 * empresa adiantou ao técnico e ainda está aberto (ele desconta na próxima
 * fatura), mais os compensados dos últimos 120 dias.
 */
function TechAdvancesBlock() {
  const t = useT();
  const q = trpc.technicianPortal.myAdvances.useQuery();
  const [showHist, setShowHist] = useState(false);
  const rows = q.data?.rows ?? [];
  const abertos = rows.filter((r) => r.status === "open");
  const hist = rows.filter((r) => r.status !== "open");
  if (!q.data || rows.length === 0) return null;
  const total = q.data.openTotal;
  return (
    <div
      className="mb-4 rounded-xl border border-amber-500/40 bg-amber-500/5 p-4"
      data-testid="tech-advances"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-amber-300">{t("techPortal.advTitle")}</h3>
        {abertos.length > 0 && (
          <span
            className="font-mono text-base font-bold text-amber-300"
            data-testid="tech-advances-total"
          >
            {formatMoney(total, { decimals: 2 })}
          </span>
        )}
      </div>
      {abertos.length > 0 ? (
        <>
          <p className="mt-1 text-xs text-neutral-300">{t("techPortal.advHint")}</p>
          <ul className="mt-2 divide-y divide-neutral-800 text-xs">
            {abertos.map((a) => (
              <li
                key={a.id}
                className="flex items-center gap-2 py-1.5"
                data-testid={`tech-adv-${a.id}`}
              >
                <span className="font-mono text-neutral-400">{a.occurredAt}</span>
                <span className="flex-1 truncate text-neutral-100">{a.description}</span>
                <span className="font-mono tabular-nums text-neutral-100">
                  {formatMoney(a.amount, { decimals: 2 })}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="mt-1 text-xs text-neutral-400">{t("techPortal.advNoneOpen")}</p>
      )}
      {hist.length > 0 && (
        <div className="mt-2">
          <button
            onClick={() => setShowHist((v) => !v)}
            className="text-[11px] text-neutral-400 hover:underline"
            data-testid="tech-adv-hist-btn"
          >
            {showHist ? "▾" : "▸"} {t("techPortal.advHistory")} ({hist.length})
          </button>
          {showHist && (
            <ul className="mt-1 divide-y divide-neutral-800 text-xs text-neutral-400">
              {hist.map((a) => (
                <li key={a.id} className="flex items-center gap-2 py-1">
                  <span className="font-mono">{a.occurredAt}</span>
                  <span className="flex-1 truncate">{a.description}</span>
                  <span className="whitespace-nowrap">
                    {t("techPortal.advDeductedIn")} {a.proformaReference ?? "—"}
                  </span>
                  <span className="font-mono tabular-nums">
                    {formatMoney(a.amount, { decimals: 2 })}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
